import { useState, useEffect, useRef } from "react";
import supabase from "./supabaseClient";

const STRIPE_URL = "https://buy.stripe.com/7sY4gzeWJ3pS1gd67Sd7q02";

const T = {
  bg: "#0e0809",
  surface: "rgba(255,255,255,0.03)",
  border: "rgba(255,255,255,0.07)",
  rose: "#c69173",
  roseLight: "#e0b49a",
  roseDim: "rgba(198,145,115,0.15)",
  cream: "#f5ece4",
  textMid: "rgba(245,236,228,0.6)",
  textFaint: "rgba(245,236,228,0.3)",
  green: "#7ab898",
  greenDim: "rgba(122,184,152,0.10)",
};

function buildCheckinPrompt(original, checkin, weekNum) {
  return `You are a ruthlessly practical OnlyFans business coach. A creator is on week ${weekNum} of using your system. Here is their original profile and plan, plus their weekly update. Generate an updated strategy for next week.

ORIGINAL PROFILE:
${JSON.stringify(original.answers, null, 2)}

ORIGINAL PLAN DIAGNOSIS:
${original.plan_data?.diagnosis || ""}

WEEK ${weekNum} UPDATE:
- New subscribers gained: ${checkin.new_subs}
- PPV revenue this week: $${checkin.ppv_revenue}
- What they tried: ${checkin.what_worked}
- Biggest struggle: ${checkin.biggest_struggle}

Based on their results, write a focused updated plan for next week using EXACTLY these headers:

### DIAGNOSIS
3 sentences max. What do their numbers tell you? What's working, what's not?

### PRICING STRATEGY
Any pricing adjustments based on their week's performance?

### PPV PLAYBOOK
Updated PPV strategy based on what worked or didn't. New hooks to try.

### PROMOTION PLAN
Double down on what worked. Replace what didn't. Specific subreddits.

### 7-DAY PLAN
Day by day plan for next week.

### REAL TALK
One honest insight about their progress this week.`;
}

function parsePlan(text) {
  const keys = ["diagnosis", "pricing", "ppv", "promotion", "today", "week", "realtalk"];
  const headers = {
    diagnosis: /###\s*DIAGNOSIS/i,
    pricing: /###\s*PRICING STRATEGY/i,
    ppv: /###\s*PPV PLAYBOOK/i,
    promotion: /###\s*PROMOTION PLAN/i,
    today: /###\s*DO THIS TODAY/i,
    week: /###\s*7-DAY PLAN/i,
    realtalk: /###\s*REAL TALK/i,
  };
  const lines = text.split("\n");
  let current = null;
  const buffers = {};
  keys.forEach(k => buffers[k] = []);
  for (const line of lines) {
    let matched = false;
    for (const [key, rx] of Object.entries(headers)) {
      if (rx.test(line)) { current = key; matched = true; break; }
    }
    if (!matched && current) buffers[current].push(line);
  }
  const result = {};
  for (const k of keys) {
    const content = buffers[k].join("\n").trim();
    if (content) result[k] = content;
  }
  return result;
}

function extractTasks(planData) {
  const tasks = [];
  if (!planData?.week) return tasks;

  const week = planData.week;

  // Format 1: "Day 1: ..." inline or newline separated
  const dayNumSegments = week.split(/(?=Day\s+\d+\s*:)/i).filter(s => s.trim());
  if (dayNumSegments.length > 1) {
    for (const segment of dayNumSegments) {
      const dayMatch = segment.match(/Day\s+(\d+)\s*:/i);
      if (dayMatch) {
        const day = parseInt(dayMatch[1]);
        const text = segment.replace(/Day\s+\d+\s*:\s*/i, "").replace(/\.?\s*$/, "").trim();
        if (text) tasks.push({ day, text });
      }
    }
    if (tasks.length > 0) return tasks;
  }

  // Format 2: "**Monday**:", "**Tuesday**:" etc.
  const dayNames = ["monday","tuesday","wednesday","thursday","friday","saturday","sunday"];
  const dayNameRegex = /\*{0,2}(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\*{0,2}\s*:/gi;
  const namedSegments = week.split(/(?=\*{0,2}(?:monday|tuesday|wednesday|thursday|friday|saturday|sunday)\*{0,2}\s*:)/i).filter(s => s.trim());
  if (namedSegments.length > 1) {
    for (const segment of namedSegments) {
      const nameMatch = segment.match(/\*{0,2}(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\*{0,2}\s*:/i);
      if (nameMatch) {
        const day = dayNames.indexOf(nameMatch[1].toLowerCase()) + 1;
        const text = segment.replace(/\*{0,2}(?:monday|tuesday|wednesday|thursday|friday|saturday|sunday)\*{0,2}\s*:\s*/i, "").replace(/\.?\s*$/, "").trim();
        if (text) tasks.push({ day: day || tasks.length + 1, text });
      }
    }
    if (tasks.length > 0) return tasks;
  }

  // Format 3: fallback — split by newlines, take non-empty lines
  const lines = week.split("\n").filter(l => l.trim().length > 20);
  lines.slice(0, 7).forEach((l, i) => tasks.push({ day: i + 1, text: l.replace(/\*+/g, "").trim() }));

  return tasks;
}

// ─── DAILY STREAK ─────────────────────────────────────────────────────────────
// A day counts when the user completes their daily revenue action (marks a task
// done). Days are stored as local YYYY-MM-DD strings in profiles.streak_days.

function localDateStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Monday of the week containing d, as a local date string (weeks run Mon–Sun)
function mondayKey(d) {
  const t = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  t.setDate(t.getDate() - ((t.getDay() + 6) % 7));
  return localDateStr(t);
}

// Daily streak with 1 freeze per calendar week: a single missed day keeps the
// streak alive (freeze consumed, no increment); a second miss in the same week
// ends it. Today, while incomplete, is "in progress" — never counted as a miss.
function computeDailyStreak(daysArr) {
  const days = new Set(daysArr || []);
  if (days.size === 0) return { count: 0, doneToday: false, freezeDay: null };
  const today = new Date();
  const doneToday = days.has(localDateStr(today));
  const earliest = [...days].sort()[0];
  const missesPerWeek = {};
  let freezeDay = null; // most recent forgiven miss
  let count = 0;
  const cursor = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (!doneToday) cursor.setDate(cursor.getDate() - 1);
  while (localDateStr(cursor) >= earliest) {
    if (days.has(localDateStr(cursor))) {
      count++;
    } else {
      const wk = mondayKey(cursor);
      missesPerWeek[wk] = (missesPerWeek[wk] || 0) + 1;
      if (missesPerWeek[wk] > 1) break; // second miss in the same week: streak over
      if (!freezeDay) freezeDay = localDateStr(cursor);
    }
    cursor.setDate(cursor.getDate() - 1);
  }
  return { count, doneToday, freezeDay };
}

// ─── CONFETTI ─────────────────────────────────────────────────────────────────
function Confetti({ active }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    if (!active || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const particles = Array.from({ length: 60 }, () => ({
      x: Math.random() * canvas.width, y: -10,
      r: Math.random() * 5 + 2,
      color: ["#c69173","#e0b49a","#7ab898","#f5ece4"][Math.floor(Math.random() * 4)],
      speed: Math.random() * 3 + 1,
      spin: Math.random() * 0.2 - 0.1, angle: 0,
    }));
    let frame;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.y += p.speed; p.x += Math.sin(p.angle) * 1.5; p.angle += p.spin;
        ctx.fillStyle = p.color; ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2); ctx.fill();
      });
      frame = requestAnimationFrame(draw);
    };
    draw();
    const t = setTimeout(() => cancelAnimationFrame(frame), 2500);
    return () => { cancelAnimationFrame(frame); clearTimeout(t); };
  }, [active]);
  if (!active) return null;
  return <canvas ref={canvasRef} style={{ position: "fixed", inset: 0, zIndex: 200, pointerEvents: "none" }} />;
}

// ─── RICH CONTENT ─────────────────────────────────────────────────────────────
function ri(text) {
  return text.split(/\*\*([^*]+)\*\*/g).map((p, i) =>
    i % 2 === 1 ? <span key={i} style={{ color: T.cream, fontWeight: "600" }}>{p}</span> : p
  );
}

function RichContent({ text }) {
  if (!text) return null;
  const lines = text.split("\n").filter(l => l.trim());
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
      {lines.map((line, i) => {
        const num = line.match(/^(\d+)\.\s+(.*)/);
        if (num) return (
          <div key={i} style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
            <span style={{ minWidth: "20px", height: "20px", borderRadius: "50%", background: T.roseDim, border: "1px solid rgba(198,145,115,0.25)", color: T.rose, fontSize: "9px", fontFamily: "'DM Mono',monospace", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: "1px" }}>{num[1]}</span>
            <span style={{ color: T.textMid, fontSize: "13px", lineHeight: "1.65", fontFamily: "'DM Sans',sans-serif" }}>{ri(num[2])}</span>
          </div>
        );
        const bul = line.match(/^[-•]\s+(.*)/);
        if (bul) return (
          <div key={i} style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
            <span style={{ color: T.rose, fontSize: "10px", marginTop: "4px", flexShrink: 0 }}>✦</span>
            <span style={{ color: T.textMid, fontSize: "13px", lineHeight: "1.65", fontFamily: "'DM Sans',sans-serif" }}>{ri(bul[1])}</span>
          </div>
        );
        const day = line.match(/^(Day \d+)[:\s—–]+(.*)/i);
        if (day) return (
          <div key={i} style={{ display: "flex", gap: "10px", alignItems: "flex-start", background: "rgba(198,145,115,0.04)", borderRadius: "8px", padding: "8px 10px" }}>
            <span style={{ color: T.rose, fontFamily: "'DM Mono',monospace", fontSize: "9px", letterSpacing: "1px", minWidth: "36px", marginTop: "2px", flexShrink: 0 }}>{day[1].toUpperCase()}</span>
            <span style={{ color: T.textMid, fontSize: "13px", lineHeight: "1.6", fontFamily: "'DM Sans',sans-serif" }}>{ri(day[2])}</span>
          </div>
        );
        const bh = line.match(/^\*\*([^*]+)\*\*:?\s*$/);
        if (bh) return <div key={i} style={{ color: T.roseLight, fontFamily: "'DM Mono',monospace", fontSize: "10px", letterSpacing: "1.5px", textTransform: "uppercase", marginTop: "6px" }}>{bh[1]}</div>;
        return <p key={i} style={{ color: T.textMid, fontSize: "13px", lineHeight: "1.72", fontFamily: "'DM Sans',sans-serif", margin: 0 }}>{ri(line)}</p>;
      })}
    </div>
  );
}

// ─── SECTION CARD ─────────────────────────────────────────────────────────────
function SectionCard({ emoji, title, content, highlight, defaultOpen = false, badge }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!content) return null;
  return (
    <div style={{ border: `1px solid ${highlight ? "rgba(198,145,115,0.28)" : T.border}`, borderRadius: "12px", marginBottom: "10px", overflow: "hidden", background: highlight ? "rgba(198,145,115,0.04)" : T.surface }}>
      <div onClick={() => setOpen(o => !o)} style={{ padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", borderBottom: open ? `1px solid ${T.border}` : "none" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
          <span style={{ fontSize: "15px" }}>{emoji}</span>
          <span style={{ color: highlight ? T.rose : T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "10px", letterSpacing: "2px", textTransform: "uppercase" }}>{title}</span>
          {badge && <span style={{ background: "rgba(122,184,152,0.12)", border: "1px solid rgba(122,184,152,0.22)", color: T.green, fontFamily: "'DM Mono',monospace", fontSize: "8px", letterSpacing: "1px", padding: "2px 7px", borderRadius: "20px" }}>{badge}</span>}
        </div>
        {/* Visible expandability hint — collapsed cards read as openable, not empty */}
        <span style={{ color: open ? T.textFaint : T.rose, fontFamily: "'DM Mono',monospace", fontSize: "8px", letterSpacing: "1px" }}>{open ? "▲" : "VIEW ▼"}</span>
      </div>
      {open && <div style={{ padding: "16px 18px" }}><RichContent text={content} /></div>}
    </div>
  );
}

// ─── TASK ITEM ────────────────────────────────────────────────────────────────
function TaskItem({ task, onToggle, isNext, dayNumber }) {
  const [justDone, setJustDone] = useState(false);
  const handleToggle = () => {
    if (!task.completed) { setJustDone(true); setTimeout(() => setJustDone(false), 1000); }
    onToggle(task.id, task.completed);
  };
  return (
    <div onClick={handleToggle} style={{
      borderRadius: "12px", marginBottom: "8px", cursor: "pointer",
      background: task.completed ? "rgba(122,184,152,0.07)" : isNext ? "rgba(198,145,115,0.06)" : T.surface,
      border: `1px solid ${task.completed ? "rgba(122,184,152,0.18)" : isNext ? "rgba(198,145,115,0.3)" : T.border}`,
      transition: "all 0.2s", transform: justDone ? "scale(1.02)" : "scale(1)",
      padding: "14px 16px", display: "flex", alignItems: "center", gap: "14px",
    }}>
      {/* Checkbox */}
      <div style={{
        width: "22px", height: "22px", borderRadius: "6px", flexShrink: 0,
        border: `2px solid ${task.completed ? T.green : isNext ? T.rose : "rgba(255,255,255,0.15)"}`,
        background: task.completed ? T.green : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.2s",
      }}>
        {task.completed && <span style={{ color: "#1a0e10", fontSize: "13px", fontWeight: "800", lineHeight: 1 }}>✓</span>}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
          <span style={{
            fontFamily: "'DM Mono',monospace", fontSize: "9px", letterSpacing: "1.5px",
            color: task.completed ? T.green : isNext ? T.rose : T.textFaint,
          }}>
            DAY {dayNumber}
          </span>
          {isNext && !task.completed && (
            <span style={{
              background: "rgba(198,145,115,0.15)", border: "1px solid rgba(198,145,115,0.3)",
              color: T.rose, fontFamily: "'DM Mono',monospace", fontSize: "7px",
              letterSpacing: "1.5px", padding: "2px 7px", borderRadius: "20px",
            }}>TODAY</span>
          )}
          {task.completed && (
            <span style={{ color: T.green, fontFamily: "'DM Mono',monospace", fontSize: "8px", letterSpacing: "1px" }}>DONE</span>
          )}
          {justDone && (
            <span style={{ color: T.green, fontFamily: "'DM Mono',monospace", fontSize: "9px" }}>+1 ✨</span>
          )}
        </div>
        <div style={{
          color: task.completed ? T.textFaint : T.cream,
          fontSize: "13px", fontFamily: "'DM Sans',sans-serif",
          lineHeight: "1.5", textDecoration: task.completed ? "line-through" : "none",
          opacity: task.completed ? 0.5 : 1,
        }}>{task.task_text}</div>
      </div>
    </div>
  );
}

// ─── SPARKLINE ────────────────────────────────────────────────────────────────
function Sparkline({ data, color = T.rose }) {
  if (!data || data.length < 2) return null;
  const W = 80, H = 32;
  const max = Math.max(...data, 1);
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * W},${H - (v / max) * (H - 4)}`).join(" ");
  return (
    <svg width={W} height={H} style={{ display: "block" }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.7" />
      <circle cx={W} cy={H - (data[data.length - 1] / max) * (H - 4)} r="2.5" fill={color} />
    </svg>
  );
}

// ─── STAT CARD ────────────────────────────────────────────────────────────────
// Styled to match the homepage mockup stat cards: mono uppercase label,
// serif value in cream (highlight = rose italic), green ↗ trend line.
function StatCard({ label, value, trend, highlight, sparkData, sub }) {
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: "10px", padding: "14px 16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
        <div style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "9px", letterSpacing: "1.5px", textTransform: "uppercase" }}>{label}</div>
        {sparkData && sparkData.length >= 2 && <Sparkline data={sparkData} color={highlight ? T.rose : T.green} />}
      </div>
      <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "26px", fontWeight: "600", color: highlight ? T.rose : T.cream, fontStyle: highlight ? "italic" : "normal", lineHeight: 1 }}>{value}</div>
      {trend && <div style={{ color: T.green, fontFamily: "'DM Mono',monospace", fontSize: "10px", marginTop: "6px" }}>↗ {trend}</div>}
      {!trend && sub && <div style={{ color: T.textMid, fontFamily: "'DM Mono',monospace", fontSize: "10px", marginTop: "6px" }}>{sub}</div>}
    </div>
  );
}

// ─── BENCHMARK BAR ────────────────────────────────────────────────────────────
function BenchmarkBar({ label, yours, average, best, unit = "" }) {
  const max = best * 1.2;
  return (
    <div style={{ marginBottom: "14px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
        <span style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "9px", letterSpacing: "1px" }}>{label}</span>
        <span style={{ color: T.cream, fontFamily: "'DM Mono',monospace", fontSize: "9px" }}>you: <span style={{ color: T.rose }}>{unit}{yours}</span></span>
      </div>
      <div style={{ position: "relative", height: "6px", background: "rgba(255,255,255,0.05)", borderRadius: "3px" }}>
        <div style={{ position: "absolute", left: `${(average / max) * 100}%`, top: "-3px", width: "1px", height: "12px", background: "rgba(255,255,255,0.2)" }} />
        <div style={{ height: "100%", width: `${Math.min((yours / max) * 100, 100)}%`, background: yours >= average ? `linear-gradient(90deg, ${T.rose}, ${T.roseLight})` : "rgba(198,145,115,0.4)", borderRadius: "3px", transition: "width 1s ease" }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "3px" }}>
        <span style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "8px" }}>avg: {unit}{average}</span>
        <span style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "8px" }}>top 10%: {unit}{best}</span>
      </div>
    </div>
  );
}

// ─── GROWTH CHART ─────────────────────────────────────────────────────────────
function GrowthChart({ checkins }) {
  const [hovered, setHovered] = useState(null);
  if (!checkins || checkins.length === 0) return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: "12px", padding: "32px", textAlign: "center", marginBottom: "16px" }}>
      <div style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "10px" }}>Complete your first check-in to see your growth chart.</div>
    </div>
  );
  const data = [{ week: "Start", subs: 0, revenue: 0 }, ...checkins.map(c => ({ week: `W${c.week_number + 1}`, subs: c.new_subs || 0, revenue: c.ppv_revenue || 0 }))];
  const cumSubs = data.reduce((acc, d, i) => { acc.push((acc[i - 1] || 0) + d.subs); return acc; }, []);
  const cumRev = data.reduce((acc, d, i) => { acc.push((acc[i - 1] || 0) + d.revenue); return acc; }, []);
  const maxRev = Math.max(...cumRev, 1), maxSubs = Math.max(...cumSubs, 1);
  const W = 320, H = 100, pad = { l: 10, r: 10, t: 10, b: 24 };
  const iw = W - pad.l - pad.r, ih = H - pad.t - pad.b;
  const revPts = cumRev.map((v, i) => `${pad.l + (i / Math.max(cumRev.length - 1, 1)) * iw},${pad.t + ih - (v / maxRev) * ih}`).join(" ");
  const subPts = cumSubs.map((v, i) => `${pad.l + (i / Math.max(cumSubs.length - 1, 1)) * iw},${pad.t + ih - (v / maxSubs) * ih}`).join(" ");
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: "12px", padding: "16px", marginBottom: "16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
        <span style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "10px", letterSpacing: "1.5px" }}>GROWTH OVER TIME</span>
        <div style={{ display: "flex", gap: "12px" }}>
          {[{ color: T.rose, label: "REVENUE" }, { color: T.green, label: "SUBS" }].map(l => (
            <div key={l.label} style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <div style={{ width: "10px", height: "2px", background: l.color, borderRadius: "1px" }} />
              <span style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "8px" }}>{l.label}</span>
            </div>
          ))}
        </div>
      </div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow: "visible" }}>
        {[0.25, 0.5, 0.75, 1].map(t => <line key={t} x1={pad.l} x2={W - pad.r} y1={pad.t + ih * (1 - t)} y2={pad.t + ih * (1 - t)} stroke="rgba(255,255,255,0.04)" strokeWidth="1" />)}
        <polyline points={revPts} fill="none" stroke={T.rose} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <polyline points={subPts} fill="none" stroke={T.green} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        {data.map((d, i) => {
          const x = pad.l + (i / Math.max(data.length - 1, 1)) * iw;
          const yr = pad.t + ih - (cumRev[i] / maxRev) * ih;
          const ys = pad.t + ih - (cumSubs[i] / maxSubs) * ih;
          return (
            <g key={i} onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)} style={{ cursor: "pointer" }}>
              <circle cx={x} cy={yr} r={hovered === i ? 4 : 2.5} fill={T.rose} />
              <circle cx={x} cy={ys} r={hovered === i ? 4 : 2.5} fill={T.green} />
              <text x={x} y={H - 4} textAnchor="middle" fill="rgba(245,236,228,0.3)" fontSize="8" fontFamily="'DM Mono',monospace">{d.week}</text>
              {hovered === i && (
                <g>
                  <rect x={x - 32} y={yr - 30} width="64" height="24" rx="4" fill="#1a0f11" stroke="rgba(198,145,115,0.3)" strokeWidth="1" />
                  <text x={x} y={yr - 16} textAnchor="middle" fill={T.rose} fontSize="9" fontFamily="'DM Mono',monospace">${cumRev[i]}</text>
                  <text x={x} y={yr - 5} textAnchor="middle" fill={T.green} fontSize="9" fontFamily="'DM Mono',monospace">+{cumSubs[i]} subs</text>
                </g>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ─── CHECKIN WIZARD ───────────────────────────────────────────────────────────
function CheckinWizard({ onSubmit, loading, weekNum }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ new_subs: "", ppv_revenue: "", what_worked: "", biggest_struggle: "" });
  const steps = [
    { emoji: "📈", question: "How many new subscribers this week?", field: "new_subs", type: "number", placeholder: "0", context: "Count everyone who followed, even free subs.", big: true },
    { emoji: "💰", question: "How much PPV revenue did you make?", field: "ppv_revenue", type: "number", placeholder: "0", context: "Tips + PPV unlocks. Don't include subscription fees.", big: true, prefix: "$" },
    { emoji: "✅", question: "What did you actually try?", field: "what_worked", type: "textarea", placeholder: "e.g. Posted to r/GoneWild 3 times, sent first PPV at $15...", context: "Specific = better plan next week." },
    { emoji: "🤔", question: "What's your biggest struggle right now?", field: "biggest_struggle", type: "textarea", placeholder: "e.g. Reddit keeps removing posts, low PPV open rates...", context: "Your next plan is built around fixing this." },
  ];
  const cur = steps[step];
  const canNext = form[cur.field]?.toString().trim().length > 0;
  const isLast = step === steps.length - 1;
  return (
    <div>
      <div style={{ display: "flex", gap: "6px", marginBottom: "28px", alignItems: "center" }}>
        {steps.map((_, i) => <div key={i} style={{ width: i === step ? "24px" : "6px", height: "6px", borderRadius: "3px", background: i < step ? T.green : i === step ? T.rose : "rgba(255,255,255,0.08)", transition: "all 0.3s" }} />)}
        <span style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "9px", marginLeft: "6px" }}>{step + 1}/{steps.length}</span>
      </div>
      <div style={{ fontSize: "30px", marginBottom: "10px" }}>{cur.emoji}</div>
      <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "24px", color: T.cream, fontWeight: "700", marginBottom: "8px", lineHeight: 1.25 }}>{cur.question}</h3>
      <p style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "10px", marginBottom: "20px", lineHeight: "1.6" }}>{cur.context}</p>
      {cur.type === "textarea" ? (
        <textarea autoFocus value={form[cur.field]} onChange={e => setForm(f => ({ ...f, [cur.field]: e.target.value }))} placeholder={cur.placeholder}
          style={{ width: "100%", padding: "13px 14px", minHeight: "100px", background: "rgba(255,255,255,0.04)", border: `1px solid rgba(198,145,115,0.2)`, borderRadius: "8px", color: T.cream, fontSize: "13px", fontFamily: "'DM Sans',sans-serif", outline: "none", resize: "vertical", marginBottom: "16px", lineHeight: "1.6" }} />
      ) : (
        <div style={{ position: "relative", marginBottom: "16px" }}>
          {cur.prefix && <span style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: T.rose, fontFamily: "'Cormorant Garamond',serif", fontSize: "28px", fontWeight: "700" }}>{cur.prefix}</span>}
          <input autoFocus type="number" value={form[cur.field]} onChange={e => setForm(f => ({ ...f, [cur.field]: e.target.value }))} placeholder={cur.placeholder}
            onKeyDown={e => e.key === "Enter" && canNext && (isLast ? onSubmit(form) : setStep(s => s + 1))}
            style={{ width: "100%", padding: `13px 14px 13px ${cur.prefix ? "30px" : "14px"}`, background: "rgba(255,255,255,0.04)", border: `1px solid rgba(198,145,115,0.2)`, borderRadius: "8px", color: T.cream, fontSize: "32px", fontFamily: "'Cormorant Garamond',serif", outline: "none", fontWeight: "700" }} />
        </div>
      )}
      <div style={{ display: "flex", gap: "8px" }}>
        {step > 0 && <button onClick={() => setStep(s => s - 1)} style={{ padding: "13px 20px", borderRadius: "8px", background: "none", border: `1px solid ${T.border}`, color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "10px", letterSpacing: "1px", cursor: "pointer" }}>← BACK</button>}
        <button disabled={!canNext || loading} onClick={() => isLast ? onSubmit(form) : setStep(s => s + 1)}
          style={{ flex: 1, padding: "14px", background: canNext && !loading ? `linear-gradient(135deg, ${T.rose}, ${T.roseLight})` : "rgba(255,255,255,0.06)", border: "none", borderRadius: "8px", color: canNext && !loading ? "#0e0809" : T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "11px", fontWeight: "700", letterSpacing: "2px", cursor: canNext && !loading ? "pointer" : "not-allowed", transition: "all 0.2s" }}>
          {loading ? `GENERATING WEEK ${weekNum} PLAN...` : isLast ? `UNLOCK WEEK ${weekNum} PLAN →` : "NEXT →"}
        </button>
      </div>
    </div>
  );
}

// ─── PLAN REVEAL ─────────────────────────────────────────────────────────────
function PlanReveal({ checkins, weekNum, onDone }) {
  const [phase, setPhase] = useState(0);
  const [msgIdx, setMsgIdx] = useState(0);
  const msgs = ["Analyzing your week...", "Identifying growth patterns...", "Rebuilding your strategy...", `Personalizing Week ${weekNum} plan...`];
  useEffect(() => {
    const t = setInterval(() => setMsgIdx(x => x < msgs.length - 1 ? x + 1 : x), 700);
    const done = setTimeout(() => { clearInterval(t); setPhase(1); }, msgs.length * 700 + 300);
    return () => { clearInterval(t); clearTimeout(done); };
  }, []); // eslint-disable-line
  const totalSubs = checkins.reduce((s, c) => s + (c.new_subs || 0), 0);
  const totalRev = checkins.reduce((s, c) => s + (c.ppv_revenue || 0), 0);
  const percentile = totalSubs > 15 ? "top 25%" : totalSubs > 8 ? "top 40%" : "top 60%";
  if (phase === 0) return (
    <div style={{ textAlign: "center", padding: "48px 20px" }}>
      <div style={{ position: "relative", width: "48px", height: "48px", margin: "0 auto 20px" }}>
        <div style={{ position: "absolute", inset: 0, border: `1.5px solid rgba(198,145,115,0.15)`, borderTop: `1.5px solid ${T.rose}`, borderRadius: "50%", animation: "spin 0.9s linear infinite" }} />
      </div>
      <p style={{ color: T.rose, fontFamily: "'DM Mono',monospace", fontSize: "12px", letterSpacing: "1px" }}>{msgs[msgIdx]}</p>
    </div>
  );
  return (
    <div>
      <div style={{ background: T.greenDim, border: "1px solid rgba(122,184,152,0.25)", borderRadius: "12px", padding: "20px", marginBottom: "20px", textAlign: "center" }}>
        <div style={{ fontSize: "28px", marginBottom: "8px" }}>🏆</div>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "22px", color: T.green, fontWeight: "700", marginBottom: "8px" }}>Week {weekNum - 1} complete.</div>
        <div style={{ color: T.textMid, fontFamily: "'DM Sans',sans-serif", fontSize: "13px", lineHeight: "1.6" }}>
          You've gained <strong style={{ color: T.cream }}>{totalSubs} subscribers</strong> and made <strong style={{ color: T.cream }}>${totalRev}</strong> so far.<br />
          That puts you in the <strong style={{ color: T.rose }}>{percentile} of creators</strong> at this stage.
        </div>
      </div>
      <div style={{ background: "linear-gradient(135deg, rgba(198,145,115,0.08), rgba(198,145,115,0.04))", border: "1px solid rgba(198,145,115,0.22)", borderRadius: "12px", padding: "20px", marginBottom: "20px" }}>
        <div style={{ fontFamily: "'DM Mono',monospace", fontSize: "9px", color: T.rose, letterSpacing: "2px", marginBottom: "10px" }}>WEEK {weekNum} FOCUS</div>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "20px", color: T.cream, fontWeight: "700", marginBottom: "8px", lineHeight: 1.3 }}>Your Week {weekNum} strategy is ready.</div>
        <div style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "10px", lineHeight: "1.7", marginBottom: "14px" }}>Based on your numbers, your updated plan tackles your biggest struggle head-on.</div>
        <button onClick={onDone} style={{ width: "100%", padding: "13px", background: `linear-gradient(135deg, ${T.rose}, ${T.roseLight})`, border: "none", borderRadius: "8px", color: "#1a0e10", fontFamily: "'DM Mono',monospace", fontSize: "11px", fontWeight: "700", letterSpacing: "2px", cursor: "pointer" }}>
          SEE MY WEEK {weekNum} PLAN →
        </button>
      </div>
      <p style={{ textAlign: "center", color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "9px" }}>📬 Your Sunday reminder is set — we'll email you next week</p>
    </div>
  );
}

// ─── AUTH SCREEN ──────────────────────────────────────────────────────────────
function AuthScreen({ onAuth }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Product paused: new signups are disabled — existing users can still log in.
  const handle = async () => {
    setLoading(true); setError("");
    try {
      const result = await supabase.auth.signInWithPassword({ email, password });
      if (result.error) setError(result.error.message);
      else onAuth(result.data.user);
    } catch { setError("Something went wrong. Try again."); }
    setLoading(false);
  };

  // FIX: OAuth redirectTo must NOT include ?paid=true.
  // That query param is only for Stripe success redirects.
  // Sending it here would bypass the paywall check for all Google signups.
  const handleGoogle = async () => {
    // Use exact current origin so www vs non-www matches wherever the user is
    const redirectTo = window.location.origin + "/dashboard";
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo, skipBrowserRedirect: false },
    });
  };

  const inp = { width: "100%", boxSizing: "border-box", padding: "12px 14px", background: "rgba(255,255,255,0.04)", border: `1px solid ${T.border}`, borderRadius: "8px", color: T.cream, fontSize: "14px", fontFamily: "'DM Mono',monospace", outline: "none", marginBottom: "10px" };
  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=DM+Mono:wght@400;500&family=DM+Sans:wght@400;500&display=swap');`}</style>
      <div style={{ width: "100%", maxWidth: "380px", background: "rgba(20,12,14,0.9)", border: `1px solid ${T.border}`, borderRadius: "16px", padding: "36px 28px" }}>
        <div style={{ textAlign: "center", marginBottom: "28px" }}>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "13px", letterSpacing: "4px", color: T.textFaint, marginBottom: "16px" }}>✦ OF COACH ✦</div>
          <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "28px", color: T.cream, fontWeight: "700", marginBottom: "6px" }}>Welcome back</h2>
          <p style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "11px" }}>Access your dashboard</p>
        </div>
        {error && <div style={{ background: "rgba(196,85,106,0.1)", border: "1px solid rgba(196,85,106,0.3)", borderRadius: "8px", padding: "10px 14px", marginBottom: "14px", color: "#c4556a", fontFamily: "'DM Mono',monospace", fontSize: "11px" }}>{error}</div>}
        <button onClick={handleGoogle} style={{ width: "100%", padding: "13px", marginBottom: "16px", background: "white", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "8px", color: "#1f1f1f", fontFamily: "'DM Sans',sans-serif", fontSize: "14px", fontWeight: "500", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
          <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/><path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"/><path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18z"/><path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.3z"/></svg>
          Continue with Google
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
          <div style={{ flex: 1, height: "1px", background: "rgba(255,255,255,0.08)" }} />
          <span style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "10px" }}>OR</span>
          <div style={{ flex: 1, height: "1px", background: "rgba(255,255,255,0.08)" }} />
        </div>
        <input type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} style={inp} />
        <input type="password" placeholder="password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && handle()} style={inp} />
        <button onClick={handle} disabled={loading} style={{ width: "100%", padding: "14px", marginTop: "4px", background: `linear-gradient(135deg, ${T.rose}, ${T.roseLight})`, border: "none", borderRadius: "8px", color: "#1a0e10", fontFamily: "'DM Mono',monospace", fontSize: "12px", fontWeight: "700", letterSpacing: "2px", cursor: "pointer" }}>
          {loading ? "..." : "LOGIN →"}
        </button>
        <p style={{ textAlign: "center", color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "10px", marginTop: "16px", lineHeight: "1.7" }}>
          New signups are paused — <a href="/" style={{ color: T.rose, textDecoration: "underline" }}>join the waitlist</a>
        </p>
      </div>
    </div>
  );
}

// ─── IDEAS GENERATOR ──────────────────────────────────────────────────────────
function IdeasTab({ plan, answers, weekNumber }) {
  const [type, setType] = useState("captions");
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(null);
  const [genCount, setGenCount] = useState(0);
  const GEN_LIMIT = 10;
  useEffect(() => {
    try { setGenCount(parseInt(localStorage.getItem("gen_count_" + new Date().toDateString()) || "0")); } catch {}
  }, []);

  const TYPES = [
    { id: "captions", label: "Post Captions", emoji: "📸" },
    { id: "ppv", label: "PPV Captions", emoji: "🔥" },
    { id: "dm", label: "DM Scripts", emoji: "💬" },
    { id: "bio", label: "Bio Ideas", emoji: "✨" },
  ];

  const generate = async () => {
    if (genCount >= GEN_LIMIT) { setIdeas(["Daily limit reached (" + GEN_LIMIT + " generations). Come back tomorrow!"]); return; }
    setLoading(true);
    setIdeas([]);
    try {
      const niche = answers?.niche || answers?.content_type || "lifestyle";
      const subs = answers?.subscriber_count || "unknown";
      const seed = Math.random().toString(36).slice(2, 8);
      const hour = new Date().getHours();
      const timeOfDay = hour < 12 ? "morning" : hour < 17 ? "afternoon" : "evening";
      const weekStage = weekNumber <= 2 ? "brand new creator just starting out"
        : weekNumber <= 4 ? "early-stage creator building momentum"
        : weekNumber <= 8 ? "growing creator with an established audience"
        : "experienced creator with a loyal fanbase";

      const prompts = {
        captions: `Generate 8 short, flirty social media photo captions for a content creator in the "${niche}" niche. They are a ${weekStage}. Captions should be personality-driven and playful. Examples: "First post here, be gentle 🥺", "Hope this brightens your ${timeOfDay} 😈", "Feeling myself today 🔥", "Rate me honestly 👀", "Just got home and felt cute", "New here, nervous but excited 💫". Early weeks: shy/curious energy. Later weeks: confident and bold. Each under 12 words. Include emojis. Seed: ${seed}. Respond ONLY with a valid JSON array of 8 strings, no markdown.`,
        ppv: `Generate 8 UNIQUE PPV message captions for an OnlyFans creator in the "${niche}" niche. Week ${weekNumber} (${weekStage}). Vary the style — mix urgency, exclusivity, teasing, and personal connection. Some short (1 sentence), some longer. ${timeOfDay} energy. Seed: ${seed}. No explicit content. Respond ONLY with a valid JSON array of 8 different strings, no markdown.`,
        dm: `Generate 8 UNIQUE DM scripts for an OnlyFans creator in the "${niche}" niche to re-engage cold subscribers or upsell PPV. Week ${weekNumber} (${weekStage}). Vary the approach — some warm/personal, some playful, some direct. ${timeOfDay} vibe. Seed: ${seed}. Respond ONLY with a valid JSON array of 8 different strings, no markdown.`,
        bio: `Generate 8 UNIQUE OnlyFans bio variations for a ${weekStage} in the "${niche}" niche with ${subs} subscribers. Mix styles: playful, mysterious, bold, soft, direct. Each under 150 chars. Make them feel different from each other. Seed: ${seed}. Respond ONLY with a valid JSON array of 8 different strings, no markdown.`,
      };
      const res = await fetch("/api/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: [{ role: "user", content: prompts[type] }] }),
      });
      const data = await res.json();
      const text = data?.content?.[0]?.text || "[]";
      const clean = text.replace(/[`]{3}json|[`]{3}/g, "").trim();
      const parsed = JSON.parse(clean);
      setIdeas(Array.isArray(parsed) ? parsed : ["Unexpected response, try again."]);
      const newCount = genCount + 1;
      setGenCount(newCount);
      try { localStorage.setItem("gen_count_" + new Date().toDateString(), newCount); } catch {}
    } catch (e) {
      setIdeas(["Something went wrong. Try again."]);
    } finally {
      setLoading(false);
    }
  };

  const copyIdea = (idea, i) => {
    navigator.clipboard.writeText(idea);
    setCopied(i);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "16px" }}>
        {TYPES.map(t => (
          <button key={t.id} onClick={() => { if (type !== t.id) { setType(t.id); setIdeas([]); } }} style={{
            padding: "10px", borderRadius: "10px", border: `1px solid ${type === t.id ? "rgba(198,145,115,0.4)" : T.border}`,
            background: type === t.id ? "rgba(198,145,115,0.1)" : T.surface,
            color: type === t.id ? T.rose : T.textFaint,
            fontFamily: "'DM Mono',monospace", fontSize: "9px", letterSpacing: "1.5px",
            cursor: "pointer", transition: "all 0.2s",
          }}>
            {t.emoji} {t.label}
          </button>
        ))}
      </div>
      <button onClick={generate} disabled={loading} style={{
        width: "100%", padding: "13px", borderRadius: "10px",
        background: loading ? "rgba(198,145,115,0.3)" : `linear-gradient(135deg, ${T.rose}, ${T.roseLight})`,
        border: "none", color: "#1a0e10", fontFamily: "'DM Mono',monospace",
        fontSize: "10px", fontWeight: "700", letterSpacing: "2px",
        cursor: loading ? "not-allowed" : "pointer", marginBottom: "16px",
      }}>
        {loading ? "GENERATING..." : `GENERATE ${TYPES.find(t=>t.id===type)?.label.toUpperCase()}`}
      </button>
      {loading && (
        <div style={{ textAlign: "center", padding: "30px", color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "10px" }}>
          <div style={{ fontSize: "24px", marginBottom: "10px", animation: "spin 1s linear infinite", display: "inline-block" }}>✦</div>
          <div>Crafting ideas for your niche...</div>
        </div>
      )}
      {ideas.length > 0 && !loading && (
        <div>
          <div style={{ fontFamily: "'DM Mono',monospace", fontSize: "9px", color: T.textFaint, letterSpacing: "1.5px", marginBottom: "10px" }}>TAP TO COPY</div>
          {ideas.map((idea, i) => (
            <div key={i} onClick={() => copyIdea(idea, i)} style={{
              padding: "12px 14px", borderRadius: "10px", marginBottom: "8px",
              background: copied === i ? "rgba(122,184,152,0.1)" : T.surface,
              border: `1px solid ${copied === i ? "rgba(122,184,152,0.3)" : T.border}`,
              cursor: "pointer", transition: "all 0.2s",
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px",
            }}>
              <div style={{ color: T.cream, fontSize: "13px", fontFamily: "'DM Sans',sans-serif", lineHeight: "1.45", flex: 1 }}>{idea}</div>
              <div style={{ color: copied === i ? T.green : T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "9px", flexShrink: 0 }}>
                {copied === i ? "COPIED!" : "COPY"}
              </div>
            </div>
          ))}
        </div>
      )}
      {ideas.length === 0 && !loading && (
        <div style={{ textAlign: "center", padding: "32px 20px" }}>
          <div style={{ fontSize: "28px", marginBottom: "12px" }}>✦</div>
          <div style={{ color: T.cream, fontFamily: "'Cormorant Garamond',serif", fontSize: "18px", fontWeight: "600", marginBottom: "8px" }}>
            Generate ideas tailored to you
          </div>
          <div style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "10px", lineHeight: "1.9", marginBottom: "16px" }}>
            Pick a type above — captions, PPV hooks, or<br />DM openers — then hit generate.<br />
            Every idea is built around your niche and week.
          </div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: T.roseDim, border: `1px solid rgba(198,145,115,0.2)`, borderRadius: "8px", padding: "8px 14px" }}>
            <span style={{ fontSize: "12px" }}>⚡</span>
            <span style={{ color: T.rose, fontFamily: "'DM Mono',monospace", fontSize: "9px", letterSpacing: "1px" }}>5 fresh ideas · takes ~10 seconds</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── PRICING CALCULATOR TAB ──────────────────────────────────────────────────
// Lookup tables (synced with /pricing-calculator/ standalone version)
const PRICING_NICHES = {
  fitness:    { sub: 9.99,  ppvPhoto: 12, ppvVideo: 18, customMin: 10, sexting: 30, tipLow: 5,  tipHigh: 75 },
  gfe:        { sub: 12.99, ppvPhoto: 15, ppvVideo: 25, customMin: 12, sexting: 40, tipLow: 5,  tipHigh: 100 },
  cosplay:    { sub: 14.99, ppvPhoto: 18, ppvVideo: 28, customMin: 14, sexting: 45, tipLow: 10, tipHigh: 100 },
  bbw:        { sub: 11.99, ppvPhoto: 15, ppvVideo: 22, customMin: 12, sexting: 40, tipLow: 5,  tipHigh: 100 },
  mature:     { sub: 11.99, ppvPhoto: 18, ppvVideo: 25, customMin: 13, sexting: 40, tipLow: 5,  tipHigh: 100 },
  alt:        { sub: 12.99, ppvPhoto: 15, ppvVideo: 22, customMin: 12, sexting: 40, tipLow: 10, tipHigh: 100 },
  feet:       { sub: 9.99,  ppvPhoto: 15, ppvVideo: 20, customMin: 12, sexting: 35, tipLow: 5,  tipHigh: 75 },
  lifestyle:  { sub: 9.99,  ppvPhoto: 12, ppvVideo: 18, customMin: 10, sexting: 30, tipLow: 5,  tipHigh: 75 },
};

const PRICING_NICHE_LABELS = {
  fitness:   "Fitness / Body",
  gfe:       "GFE (Girlfriend Experience)",
  cosplay:   "Cosplay / Roleplay",
  bbw:       "BBW / Curvy",
  mature:    "Mature / MILF",
  alt:       "Alt / Goth / Tattooed",
  feet:      "Feet / Niche fetish",
  lifestyle: "Lifestyle / General",
};

const SUBS_TIERS = {
  "0-100":    { label: "Just starting (0-100)", multiplier: 0.85 },
  "100-300":  { label: "Building (100-300)",    multiplier: 1.0 },
  "300-1000": { label: "Growing (300-1K)",       multiplier: 1.1 },
  "1000+":    { label: "Established (1K+)",      multiplier: 1.2 },
};

const CONTENT_TYPES = {
  implied:  "Implied / Topless / Lingerie",
  explicit: "Explicit",
  mixed:    "Mixed",
};

const GOALS = {
  more_subs:        "Get more subscribers",
  per_fan_revenue:  "Increase revenue per fan",
  whales:           "Find and grow whale fans",
  retention:        "Reduce churn / improve retention",
  general:          "General growth strategy",
};

function calculateBasePrices(niche, subsTier, contentType) {
  const base = PRICING_NICHES[niche] || PRICING_NICHES.lifestyle;
  const tier = SUBS_TIERS[subsTier] || SUBS_TIERS["100-300"];
  const explicit = contentType === "explicit";
  const m = tier.multiplier * (explicit ? 1.15 : 1.0);
  return {
    subscription: Math.round(base.sub * m * 100) / 100,
    ppvPhoto:     Math.round(base.ppvPhoto * m),
    ppvVideo:     Math.round(base.ppvVideo * m),
    customPerMin: Math.round(base.customMin * m),
    sexting:      Math.round(base.sexting * m),
    tipMenuLow:   Math.round(base.tipLow * m),
    tipMenuHigh:  Math.round(base.tipHigh * m),
  };
}

function PricingTab({ userId, userEmail, profile, refreshProfile }) {
  // Initialize from saved profile or defaults
  const savedQuiz = profile?.pricing_quiz_data || {};
  const [niche, setNiche] = useState(savedQuiz.niche || "lifestyle");
  const [subsTier, setSubsTier] = useState(savedQuiz.subsTier || "100-300");
  const [contentType, setContentType] = useState(savedQuiz.contentType || "implied");
  const [currentRevenue, setCurrentRevenue] = useState(savedQuiz.currentRevenue || "");
  const [goal, setGoal] = useState(savedQuiz.goal || "general");

  const [savedPrices, setSavedPrices] = useState(profile?.saved_prices || null);
  const [strategy, setStrategy] = useState(profile?.last_strategy || null);
  const [strategyGeneratedAt, setStrategyGeneratedAt] = useState(profile?.last_strategy_generated_at || null);

  const [loadingStrategy, setLoadingStrategy] = useState(false);
  const [savingPrices, setSavingPrices] = useState(false);
  const [savedToast, setSavedToast] = useState(false);
  const [strategyError, setStrategyError] = useState(null);

  // Daily generation limit (matches IdeasTab pattern)
  const STRATEGY_LIMIT = 5;
  const [genCount, setGenCount] = useState(0);
  useEffect(() => {
    try {
      setGenCount(parseInt(localStorage.getItem("strategy_count_" + new Date().toDateString()) || "0"));
    } catch {}
  }, []);
  const remaining = Math.max(0, STRATEGY_LIMIT - genCount);
  const limitReached = remaining === 0;

  // 7-day lock: strategy locked for 7 days after generation
  // Forces user to actually try the moves before getting new ones
  const LOCK_DAYS = 7;
  const lockInfo = (() => {
    if (!strategyGeneratedAt) return { locked: false, daysLeft: 0, refreshDate: null };
    const generatedTime = new Date(strategyGeneratedAt).getTime();
    const now = Date.now();
    const daysSince = (now - generatedTime) / (1000 * 60 * 60 * 24);
    const daysLeft = Math.max(0, Math.ceil(LOCK_DAYS - daysSince));
    const refreshDate = new Date(generatedTime + LOCK_DAYS * 24 * 60 * 60 * 1000);
    return {
      locked: daysSince < LOCK_DAYS,
      daysLeft,
      refreshDate,
    };
  })();

  // Compute base prices live based on current selections
  const basePrices = calculateBasePrices(niche, subsTier, contentType);

  const generateStrategy = async () => {
    if (lockInfo.locked) {
      setStrategyError(`Your strategy is locked for ${lockInfo.daysLeft} more day${lockInfo.daysLeft === 1 ? "" : "s"}. Test the current moves before getting new ones.`);
      return;
    }
    if (limitReached) {
      setStrategyError(`Daily limit reached (${STRATEGY_LIMIT} per day). Come back tomorrow.`);
      return;
    }
    setLoadingStrategy(true);
    setStrategyError(null);
    try {
      const res = await fetch("/api/pricing-strategy", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          niche: PRICING_NICHE_LABELS[niche],
          subsTier: SUBS_TIERS[subsTier].label,
          contentType: CONTENT_TYPES[contentType],
          currentRevenue: currentRevenue ? parseInt(currentRevenue) : null,
          goal: GOALS[goal],
          currentPrices: savedPrices || basePrices,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `Server error: ${res.status}`);
      }

      const data = await res.json();
      const newStrategy = data.strategy || "";
      const newTimestamp = data.generated_at || new Date().toISOString();

      setStrategy(newStrategy);
      setStrategyGeneratedAt(newTimestamp);

      // Increment daily counter on successful generation
      const newCount = genCount + 1;
      setGenCount(newCount);
      try {
        localStorage.setItem("strategy_count_" + new Date().toDateString(), newCount);
      } catch {}

      // Save to Supabase profiles table
      const { error: updateError } = await supabase
        .from("profiles")
        .update({
          last_strategy: newStrategy,
          last_strategy_generated_at: newTimestamp,
          pricing_quiz_data: { niche, subsTier, contentType, currentRevenue, goal },
        })
        .eq("id", userId);

      if (updateError) {
        console.error("Failed to save strategy:", updateError);
        // Strategy still shows in UI even if DB save failed
      }

      if (refreshProfile) refreshProfile();
    } catch (err) {
      console.error("Strategy generation error:", err);
      setStrategyError(err.message || "Failed to generate strategy. Try again.");
    } finally {
      setLoadingStrategy(false);
    }
  };

  const savePrices = async () => {
    setSavingPrices(true);
    try {
      const pricesToSave = basePrices;
      const { error } = await supabase
        .from("profiles")
        .update({
          saved_prices: pricesToSave,
          pricing_quiz_data: { niche, subsTier, contentType, currentRevenue, goal },
        })
        .eq("id", userId);

      if (error) throw error;

      setSavedPrices(pricesToSave);
      setSavedToast(true);
      setTimeout(() => setSavedToast(false), 2000);
      if (refreshProfile) refreshProfile();
    } catch (err) {
      console.error("Save prices error:", err);
      alert("Failed to save. Try again.");
    } finally {
      setSavingPrices(false);
    }
  };

  // Render parsed strategy with section headers
  const renderStrategy = (text) => {
    if (!text) return null;
    const sections = text.split(/###\s+/).filter(s => s.trim());
    return sections.map((section, i) => {
      const lines = section.split("\n");
      const title = lines[0]?.trim();
      const content = lines.slice(1).join("\n").trim();
      const isMove = /^MOVE\s+\d+/i.test(title);
      const isRealTalk = /REAL\s*TALK/i.test(title);
      return (
        <div key={i} style={{
          padding: "14px 16px",
          marginBottom: "10px",
          borderRadius: "10px",
          background: isRealTalk ? "rgba(196,85,106,0.06)" : T.surface,
          border: `1px solid ${isRealTalk ? "rgba(196,85,106,0.25)" : T.border}`,
          borderLeft: `3px solid ${isRealTalk ? "#c4556a" : isMove ? T.rose : T.green}`,
        }}>
          <div style={{
            fontFamily: "'DM Mono',monospace",
            fontSize: "9px",
            letterSpacing: "2px",
            color: isRealTalk ? "#c4556a" : T.rose,
            marginBottom: "8px",
            textTransform: "uppercase",
          }}>
            {title}
          </div>
          <div style={{
            fontFamily: "'DM Sans',sans-serif",
            fontSize: "13px",
            color: T.cream,
            lineHeight: "1.6",
            whiteSpace: "pre-wrap",
          }}>
            {content}
          </div>
        </div>
      );
    });
  };

  return (
    <div>
      {/* QUIZ INPUTS */}
      <div style={{ marginBottom: "20px" }}>
        <div style={{
          fontFamily: "'DM Mono',monospace",
          fontSize: "9px",
          color: T.textFaint,
          letterSpacing: "2px",
          marginBottom: "12px",
        }}>
          YOUR PROFILE
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "10px" }}>
          {/* Niche dropdown */}
          <div>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: "8px", color: T.textFaint, letterSpacing: "1.5px", marginBottom: "5px" }}>NICHE</div>
            <select value={niche} onChange={e => setNiche(e.target.value)} style={{
              width: "100%", padding: "10px", borderRadius: "8px",
              background: T.surface, border: `1px solid ${T.border}`,
              color: T.cream, fontFamily: "'DM Sans',sans-serif", fontSize: "12px", cursor: "pointer",
            }}>
              {Object.entries(PRICING_NICHE_LABELS).map(([k, v]) => (
                <option key={k} value={k} style={{ background: T.bg }}>{v}</option>
              ))}
            </select>
          </div>

          {/* Subs tier */}
          <div>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: "8px", color: T.textFaint, letterSpacing: "1.5px", marginBottom: "5px" }}>SUBSCRIBERS</div>
            <select value={subsTier} onChange={e => setSubsTier(e.target.value)} style={{
              width: "100%", padding: "10px", borderRadius: "8px",
              background: T.surface, border: `1px solid ${T.border}`,
              color: T.cream, fontFamily: "'DM Sans',sans-serif", fontSize: "12px", cursor: "pointer",
            }}>
              {Object.entries(SUBS_TIERS).map(([k, v]) => (
                <option key={k} value={k} style={{ background: T.bg }}>{v.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "10px" }}>
          {/* Content type */}
          <div>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: "8px", color: T.textFaint, letterSpacing: "1.5px", marginBottom: "5px" }}>CONTENT</div>
            <select value={contentType} onChange={e => setContentType(e.target.value)} style={{
              width: "100%", padding: "10px", borderRadius: "8px",
              background: T.surface, border: `1px solid ${T.border}`,
              color: T.cream, fontFamily: "'DM Sans',sans-serif", fontSize: "12px", cursor: "pointer",
            }}>
              {Object.entries(CONTENT_TYPES).map(([k, v]) => (
                <option key={k} value={k} style={{ background: T.bg }}>{v}</option>
              ))}
            </select>
          </div>

          {/* Goal */}
          <div>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: "8px", color: T.textFaint, letterSpacing: "1.5px", marginBottom: "5px" }}>GOAL</div>
            <select value={goal} onChange={e => setGoal(e.target.value)} style={{
              width: "100%", padding: "10px", borderRadius: "8px",
              background: T.surface, border: `1px solid ${T.border}`,
              color: T.cream, fontFamily: "'DM Sans',sans-serif", fontSize: "12px", cursor: "pointer",
            }}>
              {Object.entries(GOALS).map(([k, v]) => (
                <option key={k} value={k} style={{ background: T.bg }}>{v}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Optional revenue */}
        <div>
          <div style={{ fontFamily: "'DM Mono',monospace", fontSize: "8px", color: T.textFaint, letterSpacing: "1.5px", marginBottom: "5px" }}>CURRENT MONTHLY REVENUE (optional, for better strategy)</div>
          <input
            type="number"
            value={currentRevenue}
            onChange={e => setCurrentRevenue(e.target.value)}
            placeholder="e.g. 1200"
            style={{
              width: "100%", padding: "10px", borderRadius: "8px",
              background: T.surface, border: `1px solid ${T.border}`,
              color: T.cream, fontFamily: "'DM Sans',sans-serif", fontSize: "12px",
            }}
          />
        </div>
      </div>

      {/* RECOMMENDED PRICES (instant from lookup) */}
      <div style={{
        background: `linear-gradient(135deg, rgba(198,145,115,0.1), rgba(180,100,120,0.05))`,
        border: `1px solid rgba(198,145,115,0.25)`,
        borderRadius: "12px",
        padding: "18px",
        marginBottom: "16px",
      }}>
        <div style={{
          fontFamily: "'DM Mono',monospace",
          fontSize: "9px",
          letterSpacing: "2px",
          color: T.rose,
          marginBottom: "14px",
        }}>
          ✦ YOUR RECOMMENDED PRICES
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "12px" }}>
          <PriceRow label="Subscription" value={`$${basePrices.subscription}`} />
          <PriceRow label="PPV Photos" value={`$${basePrices.ppvPhoto}`} />
          <PriceRow label="PPV Videos" value={`$${basePrices.ppvVideo}`} />
          <PriceRow label="Custom / min" value={`$${basePrices.customPerMin}`} />
          <PriceRow label="Sexting / 30min" value={`$${basePrices.sexting}`} />
          <PriceRow label="Tip Menu" value={`$${basePrices.tipMenuLow} - $${basePrices.tipMenuHigh}`} />
        </div>

        <button onClick={savePrices} disabled={savingPrices} style={{
          width: "100%", padding: "11px", borderRadius: "8px",
          background: savedToast ? "rgba(122,184,152,0.15)" : T.surface,
          border: `1px solid ${savedToast ? "rgba(122,184,152,0.4)" : T.border}`,
          color: savedToast ? T.green : T.cream,
          fontFamily: "'DM Mono',monospace", fontSize: "10px",
          fontWeight: "600", letterSpacing: "1.5px",
          cursor: savingPrices ? "not-allowed" : "pointer",
          transition: "all 0.2s",
        }}>
          {savingPrices ? "SAVING..." : savedToast ? "✓ SAVED" : savedPrices ? "UPDATE SAVED PRICES" : "SAVE THESE PRICES"}
        </button>
      </div>

      {/* AI STRATEGY SECTION */}
      <div style={{ marginBottom: "16px" }}>
        <div style={{
          fontFamily: "'DM Mono',monospace",
          fontSize: "9px",
          color: T.textFaint,
          letterSpacing: "2px",
          marginBottom: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}>
          <span>YOUR AI STRATEGY</span>
          {strategyGeneratedAt && (() => {
            const generated = new Date(strategyGeneratedAt);
            const daysSince = Math.floor((Date.now() - generated.getTime()) / (1000 * 60 * 60 * 24));
            const label = daysSince === 0 ? "Generated today"
              : daysSince === 1 ? "Generated yesterday"
              : `Generated ${daysSince} days ago`;
            return (
              <span style={{ color: T.textFaint, fontSize: "8px", textTransform: "none", letterSpacing: "0.5px" }}>
                {label}
              </span>
            );
          })()}
        </div>

        {!strategy && !loadingStrategy && (
          <div style={{
            padding: "24px 18px",
            background: T.surface,
            border: `1px dashed ${T.border}`,
            borderRadius: "12px",
            textAlign: "center",
          }}>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontStyle: "italic", color: T.cream, fontSize: "16px", marginBottom: "6px" }}>
              Get your personalized pricing strategy
            </div>
            <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "12px", color: T.textFaint, marginBottom: "16px", lineHeight: "1.5" }}>
              3 specific tactical moves based on your niche, subs, and goal.<br/>
              <span style={{ color: T.textFaint, fontSize: "11px" }}>Limited to {STRATEGY_LIMIT} generations per day.</span>
            </div>
            <button onClick={generateStrategy} disabled={limitReached} style={{
              padding: "12px 24px", borderRadius: "8px",
              background: limitReached ? "rgba(198,145,115,0.2)" : `linear-gradient(135deg, ${T.rose}, ${T.roseLight})`,
              border: "none", color: limitReached ? T.textFaint : "#1a0e10",
              fontFamily: "'DM Mono',monospace", fontSize: "10px",
              fontWeight: "700", letterSpacing: "1.5px",
              cursor: limitReached ? "not-allowed" : "pointer",
            }}>
              {limitReached ? "DAILY LIMIT REACHED" : `✦ GENERATE STRATEGY (${remaining}/${STRATEGY_LIMIT} TODAY)`}
            </button>
          </div>
        )}

        {loadingStrategy && (
          <div style={{ textAlign: "center", padding: "30px", color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "10px" }}>
            <div style={{ fontSize: "24px", marginBottom: "10px", animation: "spin 1s linear infinite", display: "inline-block" }}>✦</div>
            <div>Crafting your strategy...</div>
          </div>
        )}

        {strategyError && (
          <div style={{
            padding: "14px 16px",
            background: "rgba(196,85,106,0.08)",
            border: "1px solid rgba(196,85,106,0.3)",
            borderRadius: "10px",
            color: "#c4556a",
            fontFamily: "'DM Sans',sans-serif",
            fontSize: "13px",
            marginBottom: "12px",
          }}>
            {strategyError}
          </div>
        )}

        {strategy && !loadingStrategy && (
          <>
            {renderStrategy(strategy)}
            {lockInfo.locked ? (
              <div style={{
                marginTop: "4px",
                padding: "12px 16px",
                borderRadius: "8px",
                background: "rgba(122,184,152,0.06)",
                border: "1px solid rgba(122,184,152,0.2)",
                textAlign: "center",
              }}>
                <div style={{
                  fontFamily: "'DM Mono',monospace",
                  fontSize: "10px",
                  color: T.green,
                  letterSpacing: "1.5px",
                  marginBottom: "4px",
                }}>
                  ✦ STRATEGY LOCKED FOR {lockInfo.daysLeft} MORE DAY{lockInfo.daysLeft === 1 ? "" : "S"}
                </div>
                <div style={{
                  fontFamily: "'DM Sans',sans-serif",
                  fontSize: "11px",
                  color: T.textFaint,
                  lineHeight: "1.4",
                }}>
                  Test these moves first. New strategy unlocks on {lockInfo.refreshDate.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}.
                </div>
              </div>
            ) : (
              <button onClick={generateStrategy} disabled={limitReached} style={{
                width: "100%", padding: "11px", borderRadius: "8px",
                background: T.surface, border: `1px solid ${T.border}`,
                color: limitReached ? T.textFaint : T.cream, fontFamily: "'DM Mono',monospace",
                fontSize: "10px", fontWeight: "600", letterSpacing: "1.5px",
                cursor: limitReached ? "not-allowed" : "pointer", marginTop: "4px", transition: "all 0.2s",
              }}>
                {limitReached ? `LIMIT REACHED — RESETS TOMORROW` : `↻ GENERATE NEW STRATEGY (${remaining}/${STRATEGY_LIMIT} LEFT TODAY)`}
              </button>
            )}
          </>
        )}
      </div>

      {/* DETAILED PRICING BREAKDOWN — collapsed by default */}
      <details style={{
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: "10px",
        padding: "14px 16px",
      }}>
        <summary style={{
          fontFamily: "'DM Mono',monospace",
          fontSize: "10px",
          color: T.cream,
          letterSpacing: "1.5px",
          cursor: "pointer",
          userSelect: "none",
        }}>
          📊 PRICING BREAKDOWN & EXAMPLES
        </summary>
        <div style={{ marginTop: "14px", fontFamily: "'DM Sans',sans-serif", fontSize: "13px", color: T.cream, lineHeight: "1.7" }}>
          <div style={{ marginBottom: "12px" }}>
            <strong style={{ color: T.rose }}>Subscription strategy</strong><br/>
            Charge ${basePrices.subscription}/mo as your base. Run free promos for new fans (30-day discount, 50% off first month). Grandfather existing fans when raising prices — never alienate loyal subs.
          </div>
          <div style={{ marginBottom: "12px" }}>
            <strong style={{ color: T.rose }}>PPV pricing</strong><br/>
            Photos: ${basePrices.ppvPhoto} for solo content, bundle 3-5 photos at ${Math.round(basePrices.ppvPhoto * 2.5)}.<br/>
            Videos: ${basePrices.ppvVideo} for 3-5 minute content, ${Math.round(basePrices.ppvVideo * 1.8)} for 8-12 min.
          </div>
          <div style={{ marginBottom: "12px" }}>
            <strong style={{ color: T.rose }}>Custom content</strong><br/>
            ${basePrices.customPerMin}/min minimum, with a $50-100 minimum order. Always charge upfront. Always require detailed brief before producing.
          </div>
          <div style={{ marginBottom: "12px" }}>
            <strong style={{ color: T.rose }}>Tip menu</strong><br/>
            5-tier menu from ${basePrices.tipMenuLow} to ${basePrices.tipMenuHigh}. Include both low-tier interactive items ($5-15) and high-tier exclusive items ($75-100). Pin as DM template for new fans.
          </div>
          <div>
            <strong style={{ color: T.rose }}>Sexting</strong><br/>
            ${basePrices.sexting} for 30 minutes, ${Math.round(basePrices.sexting * 1.7)} for 1 hour. Set strict boundaries on availability — ideally specific hours.
          </div>
        </div>
      </details>
    </div>
  );
}

function PriceRow({ label, value }) {
  return (
    <div style={{
      padding: "8px 10px",
      background: "rgba(255,255,255,0.025)",
      borderRadius: "8px",
      display: "flex",
      flexDirection: "column",
      gap: "2px",
    }}>
      <div style={{
        fontFamily: "'DM Mono',monospace",
        fontSize: "8px",
        color: T.textFaint,
        letterSpacing: "1px",
      }}>
        {label}
      </div>
      <div style={{
        fontFamily: "'Cormorant Garamond',serif",
        fontStyle: "italic",
        fontSize: "20px",
        color: T.cream,
        fontWeight: "600",
      }}>
        {value}
      </div>
    </div>
  );
}

// ─── ONBOARDING QUESTIONNAIRE (in-dashboard) ──────────────────────────────────
// Mirrors the homepage quiz (STEPS + buildPrompt in src/App.js). It lives here
// because the homepage bounces any signed-in session straight to /dashboard,
// so a paid user without a plan could never reach the homepage questionnaire.
const ONBOARDING_STEPS = [
  {
    id: "page_type",
    question: "Is your OnlyFans page free or paid?",
    subtitle: "This completely changes your monetization strategy.",
    type: "single",
    options: [
      { label: "Free page", sub: "I rely on PPV & tips" },
      { label: "Paid subscription", sub: "Fans pay monthly to access" },
      { label: "Both", sub: "I run free + a premium tier" },
      { label: "Not live yet", sub: "Still setting up" },
    ],
  },
  {
    id: "sub_price",
    question: "What's your current subscription price?",
    subtitle: "If free, what do you charge for PPV on average?",
    type: "single",
    options: [
      { label: "Free / $0" },
      { label: "$3 – $7" },
      { label: "$8 – $14" },
      { label: "$15 – $25" },
      { label: "$25+" },
    ],
  },
  {
    id: "subscribers",
    question: "How many paying subscribers right now?",
    subtitle: "Be honest — this determines the whole strategy.",
    type: "single",
    options: [
      { label: "0", sub: "Just started or not live" },
      { label: "1 – 20", sub: "Early stage" },
      { label: "21 – 75", sub: "Getting traction" },
      { label: "76 – 200", sub: "Growing" },
      { label: "200+", sub: "Established" },
    ],
  },
  {
    id: "monthly_income",
    question: "Roughly how much are you making per month?",
    subtitle: "Include subs, PPV, tips, and customs.",
    type: "single",
    options: [
      { label: "$0" },
      { label: "Under $100" },
      { label: "$100 – $500" },
      { label: "$500 – $2,000" },
      { label: "$2,000+" },
    ],
  },
  {
    id: "promotion",
    question: "Where do you currently promote your OnlyFans?",
    subtitle: "Select everything you actively use.",
    type: "multi",
    options: [
      { label: "Reddit" },
      { label: "Twitter / X" },
      { label: "TikTok" },
      { label: "Instagram" },
      { label: "Telegram" },
      { label: "Nowhere yet" },
    ],
  },
  {
    id: "content_type",
    question: "What kind of content do you post?",
    subtitle: "This affects which platforms and subreddits are right for you.",
    type: "single",
    options: [
      { label: "Explicit / Adult" },
      { label: "Implied / Suggestive", sub: "Sexy but not explicit — lingerie, teasing" },
      { label: "Fetish / Niche", sub: "Specific kink or category" },
      { label: "Fitness / Wellness" },
      { label: "Cosplay / Alt" },
    ],
  },
  {
    id: "ppv",
    question: "Do you send PPV (pay-per-view) messages to fans?",
    subtitle: "PPV is often where the real money is made.",
    type: "single",
    options: [
      { label: "Never / don't know how" },
      { label: "Occasionally", sub: "A few times a month" },
      { label: "Regularly", sub: "Weekly or more" },
      { label: "It's my main income" },
    ],
  },
  {
    id: "biggest_problem",
    question: "What's your #1 problem right now?",
    subtitle: "Pick the one that keeps you up at night.",
    type: "single",
    options: [
      { label: "Not enough subscribers" },
      { label: "Subscribers don't spend money" },
      { label: "Don't know what to charge" },
      { label: "Burning out on content" },
      { label: "Don't know how to promote" },
    ],
  },
];

function buildOnboardingPrompt(a) {
  return `You are a ruthlessly practical OnlyFans business coach. You know the platform inside out — PPV mechanics, mass messaging, tip menus, renewal psychology, subreddit strategies, churn reduction, everything. You give specific, honest, actionable advice. No motivational fluff. No vague tips. Real numbers. Real tactics.

CREATOR PROFILE:
- Page type: ${a.page_type}
- Subscription price: ${a.sub_price}
- Paying subscribers: ${a.subscribers}
- Monthly income: ${a.monthly_income}
- Current promotion channels: ${Array.isArray(a.promotion) ? a.promotion.join(", ") : a.promotion}
- Content type: ${a.content_type}
- PPV usage: ${a.ppv}
- Biggest problem: ${a.biggest_problem}

Write their personalized OnlyFans strategy using EXACTLY these 7 section headers (copy them verbatim):

### DIAGNOSIS
MAXIMUM 3 SENTENCES. Not 4. Not 5. Exactly 3. Each sentence must be punchy and specific. Sentence 1: their exact situation in numbers. Sentence 2: the real reason they're stuck (not what they said, the deeper truth). Sentence 3: what that means for their income right now. Do NOT use filler words. Do NOT be gentle. Reference their actual numbers.

### PRICING STRATEGY
Give them exact numbers. What should their sub price be? What should PPV messages cost ($X for photos, $X for videos, $X for customs)? Should they run a free page or paid? What's their optimal tip menu setup? Reference their current niche and subscriber count when giving numbers.

### PPV PLAYBOOK
This is the highest-leverage section. Give them a specific PPV strategy for their situation. How often to send? What script structure works? What content converts best for their niche? Give them 2-3 example PPV message hooks they can use immediately (keep tasteful but effective). Explain the "PPV funnel" — free teaser → PPV offer → upsell to custom.

### PROMOTION PLAN
Based on their content type, give them the exact top 3 places to promote. For Reddit: name specific subreddits that match their niche (be specific — r/actual_subreddit_name). For Twitter: posting cadence and strategy. For TikTok: how to drive without getting banned. Be concrete about what to post where.

### DO THIS TODAY
Exactly 4 actions they can complete in the next 3 hours. Number them 1-4. Be ultra-specific — not "post on Reddit" but "Post a teaser photo to r/[subreddit] with this caption style: [example]". Each action should take 15-30 minutes.

### 7-DAY PLAN
Day by day. Day 1 through Day 7. One focused task per day. Each task should build on the last. By day 7 they should have a complete promotion + monetization system running.

### REAL TALK
The one thing nobody tells creators in their exact situation. Be honest, not harsh. This should feel like advice from a friend who knows the industry — something that reframes their thinking or addresses a blind spot specific to their numbers and problem.

Keep each section focused and specific to their profile. No generic advice. If they have 0 subscribers, speak to that reality. If they're making $0, acknowledge it and address it directly. The advice should feel like it was written for this exact person.`;
}

function OnboardingQuiz({ onComplete }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [multiSel, setMultiSel] = useState([]);
  const [phase, setPhase] = useState("quiz"); // quiz | loading | error
  const [msgIdx, setMsgIdx] = useState(0);
  const msgs = ["Analyzing your profile...", "Benchmarking your niche...", "Researching subreddits for your niche...", "Writing your Week 1 plan..."];

  useEffect(() => {
    if (phase !== "loading") return;
    const t = setInterval(() => setMsgIdx(x => Math.min(x + 1, msgs.length - 1)), 1200);
    return () => clearInterval(t);
  }, [phase]); // eslint-disable-line

  const step = ONBOARDING_STEPS[stepIdx];
  const isLast = stepIdx === ONBOARDING_STEPS.length - 1;

  const generate = async (finalAnswers) => {
    setPhase("loading");
    setMsgIdx(0);
    try {
      const res = await fetch("/api/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: [{ role: "user", content: buildOnboardingPrompt(finalAnswers) }] }),
      });
      if (!res.ok) throw new Error(`API error: ${res.status}`);
      const data = await res.json();
      const text = data.content?.map(b => b.text || "").join("\n") || "";
      if (!text.trim()) throw new Error("Empty response");
      await onComplete(parsePlan(text), finalAnswers);
    } catch (e) {
      console.error("Onboarding generation failed:", e);
      setPhase("error");
    }
  };

  const submit = (value) => {
    const next = { ...answers, [step.id]: value };
    setAnswers(next);
    if (isLast) generate(next);
    else { setStepIdx(i => i + 1); setMultiSel([]); }
  };

  if (phase === "loading") return (
    <div style={{ textAlign: "center", padding: "60px 20px" }}>
      <div style={{ position: "relative", width: "48px", height: "48px", margin: "0 auto 20px" }}>
        <div style={{ position: "absolute", inset: 0, border: "1.5px solid rgba(198,145,115,0.15)", borderTop: `1.5px solid ${T.rose}`, borderRadius: "50%", animation: "spin 0.9s linear infinite" }} />
      </div>
      <p style={{ color: T.rose, fontFamily: "'DM Mono',monospace", fontSize: "12px", letterSpacing: "1px" }}>{msgs[msgIdx]}</p>
      <p style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "9px", marginTop: "10px" }}>Takes about 20 seconds</p>
    </div>
  );

  if (phase === "error") return (
    <div style={{ textAlign: "center", padding: "40px 20px" }}>
      <div style={{ fontSize: "28px", marginBottom: "12px" }}>😕</div>
      <div style={{ color: T.cream, fontFamily: "'Cormorant Garamond',serif", fontSize: "20px", fontWeight: "700", marginBottom: "8px" }}>Something went wrong</div>
      <div style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "10px", marginBottom: "20px" }}>Your answers are saved — try generating again.</div>
      <button onClick={() => generate(answers)} style={{ padding: "13px 28px", background: `linear-gradient(135deg, ${T.rose}, ${T.roseLight})`, border: "none", borderRadius: "8px", color: "#1a0e10", fontFamily: "'DM Mono',monospace", fontSize: "11px", fontWeight: "700", letterSpacing: "2px", cursor: "pointer" }}>TRY AGAIN →</button>
    </div>
  );

  const isSelected = (label) => multiSel.includes(label);
  return (
    <div>
      <div style={{ display: "flex", gap: "6px", marginBottom: "24px", alignItems: "center" }}>
        {ONBOARDING_STEPS.map((_, i) => <div key={i} style={{ width: i === stepIdx ? "20px" : "6px", height: "6px", borderRadius: "3px", background: i < stepIdx ? T.green : i === stepIdx ? T.rose : "rgba(255,255,255,0.08)", transition: "all 0.3s" }} />)}
        <span style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "9px", marginLeft: "6px" }}>{stepIdx + 1}/{ONBOARDING_STEPS.length}</span>
      </div>
      <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "24px", color: T.cream, fontWeight: "700", marginBottom: "6px", lineHeight: 1.25 }}>{step.question}</h3>
      <p style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "10px", marginBottom: "20px", lineHeight: "1.6" }}>{step.subtitle}</p>
      {step.options.map((opt, i) => (
        <button
          key={i}
          onClick={() => {
            if (step.type === "multi") setMultiSel(sel => isSelected(opt.label) ? sel.filter(l => l !== opt.label) : [...sel, opt.label]);
            else submit(opt.label);
          }}
          style={{
            display: "block", width: "100%", textAlign: "left", padding: "13px 16px", marginBottom: "8px",
            background: step.type === "multi" && isSelected(opt.label) ? "rgba(198,145,115,0.12)" : T.surface,
            border: `1px solid ${step.type === "multi" && isSelected(opt.label) ? "rgba(198,145,115,0.45)" : T.border}`,
            borderRadius: "10px", cursor: "pointer", transition: "all 0.15s",
          }}
        >
          <span style={{ color: T.cream, fontFamily: "'DM Sans',sans-serif", fontSize: "14px", fontWeight: "500" }}>{opt.label}</span>
          {opt.sub && <span style={{ display: "block", color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "9px", marginTop: "3px" }}>{opt.sub}</span>}
        </button>
      ))}
      <div style={{ display: "flex", gap: "8px", marginTop: "14px" }}>
        {stepIdx > 0 && (
          <button onClick={() => { setStepIdx(i => i - 1); setMultiSel([]); }} style={{ padding: "12px 18px", borderRadius: "8px", background: "none", border: `1px solid ${T.border}`, color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "10px", letterSpacing: "1px", cursor: "pointer" }}>← BACK</button>
        )}
        {step.type === "multi" && (
          <button disabled={multiSel.length === 0} onClick={() => submit(multiSel)} style={{ flex: 1, padding: "13px", background: multiSel.length > 0 ? `linear-gradient(135deg, ${T.rose}, ${T.roseLight})` : "rgba(255,255,255,0.06)", border: "none", borderRadius: "8px", color: multiSel.length > 0 ? "#1a0e10" : T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "11px", fontWeight: "700", letterSpacing: "2px", cursor: multiSel.length > 0 ? "pointer" : "not-allowed" }}>
            {isLast ? "BUILD MY PLAN →" : "CONTINUE →"}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── NEW USER ONBOARDING SCREEN ───────────────────────────────────────────────
function NewUserScreen({ userEmail, onLogout, welcome, onComplete }) {
  const [building, setBuilding] = useState(false);
  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=DM+Mono:wght@400;500&family=DM+Sans:wght@400;500&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
      <div style={{ width: "100%", maxWidth: "420px" }}>
        <div style={{ textAlign: "center", marginBottom: building ? "24px" : "36px" }}>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "13px", letterSpacing: "4px", color: T.textFaint, marginBottom: "20px" }}>✦ OF COACH ✦</div>
          {welcome && !building && (
            <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: T.greenDim, border: "1px solid rgba(122,184,152,0.3)", borderRadius: "20px", padding: "5px 14px", marginBottom: "16px" }}>
              <span style={{ color: T.green, fontFamily: "'DM Mono',monospace", fontSize: "9px", letterSpacing: "1.5px" }}>✓ PAYMENT CONFIRMED — YOU'RE IN</span>
            </div>
          )}
        </div>

        {building ? (
          <OnboardingQuiz onComplete={onComplete} />
        ) : (<>
          <div style={{ textAlign: "center", marginBottom: "36px" }}>
            <div style={{ fontSize: "40px", marginBottom: "16px" }}>🎯</div>
            <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "30px", color: T.cream, fontWeight: "700", marginBottom: "10px", lineHeight: 1.2 }}>
              Let's build your strategy.
            </h2>
            <p style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "11px", lineHeight: "1.8", maxWidth: "320px", margin: "0 auto" }}>
              You're in. Now we need 2 minutes to build your personalized OnlyFans plan.
            </p>
          </div>

          {[
            { emoji: "💬", text: "Answer 8 quick questions about your page" },
            { emoji: "🤖", text: "Claude analyzes your situation in real-time" },
            { emoji: "📋", text: "Get your custom weekly plan + task list" },
            { emoji: "📈", text: "Check in each week for an updated strategy" },
          ].map((item, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: "14px",
              padding: "14px 16px", marginBottom: "8px",
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.07)",
              borderRadius: "10px",
            }}>
              <span style={{ fontSize: "18px", flexShrink: 0 }}>{item.emoji}</span>
              <span style={{ color: T.textMid, fontFamily: "'DM Sans',sans-serif", fontSize: "13px" }}>{item.text}</span>
            </div>
          ))}

          <button
            onClick={() => setBuilding(true)}
            style={{
              display: "block", width: "100%", marginTop: "24px",
              padding: "16px", borderRadius: "10px", textAlign: "center",
              background: `linear-gradient(135deg, ${T.rose}, ${T.roseLight})`,
              color: "#1a0e10", fontFamily: "'DM Mono',monospace",
              fontSize: "12px", fontWeight: "700", letterSpacing: "2px",
              border: "none", cursor: "pointer", boxShadow: "0 4px 20px rgba(198,145,115,0.3)",
            }}
          >
            BUILD MY STRATEGY →
          </button>
        </>)}

        <p style={{ textAlign: "center", color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "10px", marginTop: "16px" }}>
          Signed in as {userEmail}
          <span style={{ margin: "0 8px", opacity: 0.4 }}>·</span>
          <span onClick={onLogout} style={{ cursor: "pointer", textDecoration: "underline" }}>logout</span>
        </p>
      </div>
    </div>
  );
}

// ─── QUICK REVENUE LOG ────────────────────────────────────────────────────────
function QuickRevenueLog({ userId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [subs, setSubs] = useState("");
  const [rev, setRev] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    if (!subs && !rev) return;
    setSaving(true);
    const entry = {
      user_id: userId,
      week_number: 0, // 0 = manual log, not tied to a check-in week
      new_subs: parseInt(subs) || 0,
      ppv_revenue: parseFloat(rev) || 0,
      what_worked: "manual log",
      biggest_struggle: "",
      logged_at: new Date().toISOString(),
    };
    const { data } = await supabase.from("checkins").insert(entry).select().single();
    if (data) onSaved(data);
    setSubs(""); setRev("");
    setSaving(false); setSaved(true);
    setTimeout(() => { setSaved(false); setOpen(false); }, 2000);
  };

  const inp = { width: "100%", padding: "10px 12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(198,145,115,0.2)", borderRadius: "8px", color: "#f5ece4", fontFamily: "'DM Mono',monospace", fontSize: "12px", outline: "none", marginBottom: "8px" };

  return (
    <div style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${T.border}`, borderRadius: "12px", padding: "14px 16px", marginBottom: "16px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontFamily: "'DM Mono',monospace", fontSize: "9px", color: T.textFaint, letterSpacing: "1.5px", marginBottom: "2px" }}>QUICK LOG</div>
          <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "12px", color: T.textMid }}>Log revenue or subs anytime</div>
        </div>
        <button onClick={() => setOpen(o => !o)} style={{ background: open ? T.roseDim : "none", border: `1px solid rgba(198,145,115,0.25)`, borderRadius: "8px", padding: "7px 14px", color: T.rose, fontFamily: "'DM Mono',monospace", fontSize: "9px", letterSpacing: "1px", cursor: "pointer" }}>
          {open ? "CANCEL" : "+ LOG"}
        </button>
      </div>
      {open && (
        <div style={{ marginTop: "14px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "4px" }}>
            <input type="number" placeholder="New subs" value={subs} onChange={e => setSubs(e.target.value)} style={{...inp, marginBottom: 0}} />
            <input type="number" placeholder="PPV revenue $" value={rev} onChange={e => setRev(e.target.value)} style={{...inp, marginBottom: 0}} />
          </div>
          <button onClick={handleSave} disabled={saving || saved} style={{ width: "100%", marginTop: "10px", padding: "11px", borderRadius: "8px", background: saved ? "rgba(122,184,152,0.2)" : `linear-gradient(135deg,${T.rose},${T.roseLight})`, border: saved ? "1px solid rgba(122,184,152,0.3)" : "none", color: saved ? T.green : "#0e0809", fontFamily: "'DM Mono',monospace", fontSize: "10px", fontWeight: "700", letterSpacing: "2px", cursor: "pointer" }}>
            {saved ? "✓ SAVED" : saving ? "SAVING..." : "SAVE ENTRY"}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── REVENUE GOAL TRACKER ─────────────────────────────────────────────────────
function RevenueGoalTracker({ totalRev, checkins, userId }) {
  const [goal, setGoal] = useState(null);
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    supabase.from("profiles").select("revenue_goal").eq("id", userId).single()
      .then(({ data }) => {
        if (data?.revenue_goal) {
          setGoal(data.revenue_goal);
          setInput(String(data.revenue_goal));
        } else {
          setEditing(true); // first time — prompt to set goal
        }
      });
  }, [userId]);

  const saveGoal = async () => {
    const val = parseInt(input);
    if (!val || val < 1) return;
    setSaving(true);
    await supabase.from("profiles").upsert({ id: userId, revenue_goal: val }, { onConflict: "id" });
    setGoal(val);
    setEditing(false);
    setSaving(false);
  };

  const pct = goal ? Math.min(Math.round((totalRev / goal) * 100), 100) : 0;
  const remaining = goal ? Math.max(goal - totalRev, 0) : 0;
  const weeksOfData = checkins.length;
  const avgWeeklyRev = weeksOfData > 0 ? totalRev / weeksOfData : 0;
  const weeksToGoal = avgWeeklyRev > 0 && goal ? Math.ceil(remaining / avgWeeklyRev) : null;

  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: "12px", padding: "16px", marginBottom: "16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
        <div style={{ fontFamily: "'DM Mono',monospace", fontSize: "10px", color: T.textFaint, letterSpacing: "1.5px" }}>REVENUE GOAL</div>
        {goal && !editing && (
          <button onClick={() => { setEditing(true); setInput(String(goal)); }} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: "5px", padding: "3px 10px", color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "9px", cursor: "pointer", letterSpacing: "1px" }}>EDIT</button>
        )}
      </div>

      {editing ? (
        <div>
          <p style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "10px", marginBottom: "12px", lineHeight: "1.6" }}>
            What's your monthly revenue goal?
          </p>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <div style={{ position: "relative", flex: 1 }}>
              <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: T.rose, fontFamily: "'Cormorant Garamond',serif", fontSize: "22px", fontWeight: "700" }}>$</span>
              <input
                autoFocus
                type="number"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && saveGoal()}
                placeholder="500"
                style={{ width: "100%", padding: "11px 14px 11px 28px", background: "rgba(255,255,255,0.05)", border: `1px solid rgba(198,145,115,0.25)`, borderRadius: "8px", color: T.cream, fontSize: "20px", fontFamily: "'Cormorant Garamond',serif", fontWeight: "700", outline: "none" }}
              />
            </div>
            <button onClick={saveGoal} disabled={saving || !input} style={{ padding: "11px 18px", background: `linear-gradient(135deg, ${T.rose}, ${T.roseLight})`, border: "none", borderRadius: "8px", color: "#1a0e10", fontFamily: "'DM Mono',monospace", fontSize: "10px", fontWeight: "700", letterSpacing: "1px", cursor: "pointer", whiteSpace: "nowrap" }}>
              {saving ? "..." : "SET GOAL"}
            </button>
          </div>
        </div>
      ) : goal ? (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "10px" }}>
            <div>
              <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "32px", color: pct === 100 ? T.green : T.cream, fontWeight: "700" }}>${totalRev}</span>
              <span style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "11px", marginLeft: "6px" }}>/ ${goal}/mo goal</span>
            </div>
            <span style={{ fontFamily: "'DM Mono',monospace", fontSize: "14px", color: pct === 100 ? T.green : T.rose, fontWeight: "700" }}>{pct}%</span>
          </div>

          <div style={{ height: "6px", background: "rgba(255,255,255,0.06)", borderRadius: "6px", overflow: "hidden", marginBottom: "12px" }}>
            <div style={{
              height: "100%", borderRadius: "6px",
              width: `${pct}%`,
              background: pct === 100
                ? T.green
                : pct >= 70 ? `linear-gradient(90deg, ${T.rose}, #e0b49a)`
                : `linear-gradient(90deg, ${T.rose}, ${T.roseLight})`,
              transition: "width 0.6s cubic-bezier(0.4,0,0.2,1)",
              boxShadow: pct === 100 ? "0 0 8px rgba(122,184,152,0.4)" : "0 0 8px rgba(198,145,115,0.25)",
            }} />
          </div>

          {pct === 100 ? (
            <div style={{ color: T.green, fontFamily: "'DM Mono',monospace", fontSize: "10px", letterSpacing: "1px" }}>
              🏆 GOAL REACHED — time to set a new one
            </div>
          ) : (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "10px" }}>
                ${remaining} to go
              </span>
              {weeksToGoal && (
                <span style={{ color: T.rose, fontFamily: "'DM Mono',monospace", fontSize: "10px" }}>
                  ~{weeksToGoal} week{weeksToGoal !== 1 ? "s" : ""} at your current pace
                </span>
              )}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

// ─── MAIN DASHBOARD ───────────────────────────────────────────────────────────
export default function Dashboard() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [checkinLoading, setCheckinLoading] = useState(false);
  const [checkinError, setCheckinError] = useState(null); // FIX: track checkin errors
  const [activeTab, setActiveTab] = useState("plan");
  const [isPaid, setIsPaid] = useState(false);
  const [checkingPaid, setCheckingPaid] = useState(false);
  const [checkinPhase, setCheckinPhase] = useState("form");
  const [confetti, setConfetti] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [milestone, setMilestone] = useState(null); // { emoji, title, sub }
  const [profile, setProfile] = useState(null); // pricing tab data

  const showMilestone = (emoji, title, sub) => {
    setMilestone({ emoji, title, sub });
    setTimeout(() => setMilestone(null), 4000);
  };


  useEffect(() => {
    // STEP 1: Register listener BEFORE anything else.
    // onAuthStateChange fires when Supabase exchanges the OAuth hash for a session.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setUser(session?.user || null);
      setLoading(false);
      if (session && window.location.hash.includes("access_token")) {
        window.history.replaceState({}, "", "/dashboard");
      }
    });

    // STEP 2: If there's an OAuth hash in the URL, explicitly exchange it.
    // This is needed when Dashboard mounts fresh after Google redirect.
    const hash = window.location.hash;
    if (hash && hash.includes("access_token")) {
      // Parse the hash manually and set the session
      const params = new URLSearchParams(hash.replace("#", ""));
      const accessToken = params.get("access_token");
      const refreshToken = params.get("refresh_token");
      if (accessToken && refreshToken) {
        supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
          .then(({ data: { session } }) => {
            if (session) {
              setUser(session.user);
              window.history.replaceState({}, "", "/dashboard");
            }
            setLoading(false);
          });
      }
    } else {
      // STEP 3: Normal load — check for existing session
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) setUser(session.user);
        setLoading(false);
      });
    }

    return () => subscription.unsubscribe();
  }, []);

  // FIX: checkPaid now returns the paid status so loadData can be gated on it.
  // When coming from a Stripe redirect, the webhook fires asynchronously — often
  // 2–10 seconds after the user lands back on the page. We retry up to 8 times
  // with increasing delays so we don't show "No plan yet" on a race condition.
  //
  // FIX (flicker): onAuthStateChange fires repeatedly (INITIAL_SESSION, SIGNED_IN,
  // TOKEN_REFRESHED) and each event produces a new `user` object, which re-ran this
  // effect — every re-run flipped checkingPaid/dataLoaded and remounted the
  // onboarding screen. Guard by user id so the paid check + data load run once
  // per signed-in user.
  const loadedUserRef = useRef(null);
  useEffect(() => {
    if (!user) return;
    if (loadedUserRef.current === user.id) return;
    loadedUserRef.current = user.id;
    // "welcome=true" is the Stripe Payment Link post-payment redirect;
    // "paid=true" is the legacy param. Both mean: retry the paid check
    // while the webhook lands.
    const fromStripe = window.location.search.includes("paid=true") ||
      window.location.search.includes("welcome=true") ||
      sessionStorage.getItem("ofcoach_from_stripe") === "true";
    if (fromStripe) sessionStorage.setItem("ofcoach_from_stripe", "true");
    checkPaid(fromStripe).then(paid => {
      if (paid) {
        sessionStorage.removeItem("ofcoach_from_stripe");
        loadData();
      }
    });
  }, [user]); // eslint-disable-line

  const checkPaid = async (retryForStripe = false) => {
    setCheckingPaid(true);

    const maxAttempts = retryForStripe ? 8 : 1;
    const delays = [0, 1500, 2000, 2500, 3000, 3000, 3000, 3000]; // ms between retries

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (delays[attempt] > 0) {
        await new Promise(r => setTimeout(r, delays[attempt]));
      }
      const { data } = await supabase
        .from("paid_users")
        .select("active")
        .eq("email", user.email.toLowerCase())
        .single();
      if (data?.active === true) {
        setIsPaid(true);
        setCheckingPaid(false);
        return true;
      }
    }

    setIsPaid(false);
    setCheckingPaid(false);
    return false;
  };

  const loadProfile = async () => {
    if (!user?.id) return;
    const { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", user.id)
      .single();
    if (data) setProfile(data);
  };

  const loadData = async () => {
    const [plansRes, tasksRes, checkinsRes, profileRes] = await Promise.all([
      supabase.from("plans").select("*").eq("user_id", user.id).order("week_number"),
      supabase.from("tasks").select("*").eq("user_id", user.id).order("week_number"),
      supabase.from("checkins").select("*").eq("user_id", user.id).order("week_number"),
      supabase.from("profiles").select("*").eq("id", user.id).single(),
    ]);
    const existingPlans = plansRes.data || [];
    const existingTasks = tasksRes.data || [];
    const existingCheckins = checkinsRes.data || [];
    setPlans(existingPlans);
    setTasks(existingTasks);
    setCheckins(existingCheckins);
    if (profileRes.data) setProfile(profileRes.data);

    // Backfill tasks for any week that has a plan but no tasks
    if (existingPlans.length > 0) {
      const weeksWithTasks = new Set(existingTasks.map(t => t.week_number));
      const plansWithoutTasks = existingPlans.filter(p => !weeksWithTasks.has(p.week_number));
      if (plansWithoutTasks.length > 0) {
        const allNewTasks = plansWithoutTasks.flatMap(p => {
          const extracted = extractTasks(p.plan_data);
          return extracted.map(t => ({
            user_id: user.id,
            week_number: p.week_number,
            task_text: t.text,
            completed: false,
            day: t.day,
          }));
        });
        if (allNewTasks.length > 0) {
          const { data: ins } = await supabase.from("tasks").insert(allNewTasks).select();
          setTasks(prev => [...prev, ...(ins || [])]);
        }
      }
    }

    if (existingPlans.length === 0) {
      // Claim from pending_plans, keyed to THIS user's email — the only
      // automatic claim path. The old localStorage fallback is gone: it let a
      // leftover plan from a previous visitor on the same browser attach to a
      // different account (the fake-diagnosis bug). Users with no pending plan
      // build one via the in-dashboard questionnaire instead.
      const { data: pendingRow } = await supabase
        .from("pending_plans")
        .select("*")
        .eq("email", user.email.toLowerCase())
        .single();

      if (pendingRow) {
        await savePlanOnce(pendingRow.plan_data, pendingRow.answers, 1);
        await supabase.from("pending_plans").delete().eq("email", user.email.toLowerCase());
      }
    }

    // Defense-in-depth: clear the homepage's one-shot transfer keys so stale
    // plans can never resurface.
    try {
      localStorage.removeItem("ofcoach_plan");
      localStorage.removeItem("ofcoach_answers");
    } catch {}

    // FIX (flicker): set dataLoaded only after any pending plan has been claimed,
    // so the onboarding screen doesn't flash in and then vanish mid-claim.
    setDataLoaded(true);
  };

  const savePlanOnce = async (planData, answers, weekNum) => {
    // Ensure profile row exists (plans table has FK to profiles)
    await supabase.from("profiles").upsert({ id: user.id }, { onConflict: "id" });
    const { data: plan, error: planErr } = await supabase.from("plans").insert({ user_id: user.id, week_number: weekNum, answers, plan_data: planData }).select().single();
    if (planErr) { console.error("savePlanOnce error:", planErr); return; }
    const extracted = extractTasks(planData);
    let newTasks = [];
    if (extracted.length > 0) {
      const { data: ins } = await supabase.from("tasks").insert(extracted.map(t => ({ user_id: user.id, week_number: weekNum, task_text: t.text, completed: false }))).select();
      newTasks = ins || [];
    }
    if (plan) setPlans([plan]);
    setTasks(newTasks);
  };

  const savePlan = async (planData, answers, weekNum) => {
    const { data: plan } = await supabase.from("plans").insert({ user_id: user.id, week_number: weekNum, answers, plan_data: planData }).select().single();
    const extracted = extractTasks(planData);
    let newTasks = [];
    if (extracted.length > 0) {
      const { data: ins } = await supabase.from("tasks").insert(extracted.map(t => ({ user_id: user.id, week_number: weekNum, task_text: t.text, completed: false }))).select();
      newTasks = ins || [];
    }
    if (plan) setPlans(prev => [...prev, plan]);
    setTasks(prev => [...prev, ...newTasks]);

    // Milestone: first check-in ever
    if (checkins.length === 0) showMilestone("📊", "First check-in logged!", "Your progress is now being tracked. Keep going.");
  };

  // Daily streak: record today (once) when the user completes a task —
  // completing Today's Focus is completing a task. Persisted per user in
  // profiles.streak_days so the streak survives reload/login.
  const markTodayDone = async () => {
    const today = localDateStr(new Date());
    const existing = profile?.streak_days || [];
    if (existing.includes(today)) return;
    const updated = [...existing, today];
    setProfile(p => ({ ...(p || {}), streak_days: updated }));
    await supabase.from("profiles").upsert({ id: user.id, streak_days: updated }, { onConflict: "id" });
  };

  const toggleTask = async (taskId, completed) => {
    if (!completed) markTodayDone();
    setTasks(ts => {
      const updated = ts.map(t => t.id === taskId ? { ...t, completed: !completed } : t);
      const currentWk = plans.length;
      const wkTasks = updated.filter(t => t.week_number === currentWk);
      const doneCount = updated.filter(t => t.week_number === currentWk && t.completed).length;

      if (wkTasks.length > 0 && wkTasks.every(t => t.completed)) {
        setConfetti(true);
        setTimeout(() => setConfetti(false), 3000);
        showMilestone("🏆", "Week complete!", "You finished every task. Check in to unlock next week.");
      } else if (!completed && doneCount === 1) {
        // First task ever completed
        const allPrevDone = tasks.filter(t => t.week_number < currentWk && t.completed).length;
        if (allPrevDone === 0) showMilestone("⚡", "First task done!", "One down. Keep the momentum going.");
      }
      return updated;
    });
    await supabase.from("tasks").update({ completed: !completed }).eq("id", taskId);
  };

  // FIX: wrapped in try/finally so checkinLoading always clears, even on API failure.
  // FIX: added checkinError state so the user sees feedback instead of a frozen spinner.
  const handleCheckin = async (form) => {
    setCheckinLoading(true);
    setCheckinError(null);
    const weekNum = plans.length + 1;
    try {
      const { data: newCheckin } = await supabase.from("checkins").insert({
        user_id: user.id, week_number: weekNum - 1,
        new_subs: parseInt(form.new_subs) || 0,
        ppv_revenue: parseFloat(form.ppv_revenue) || 0,
        what_worked: form.what_worked,
        biggest_struggle: form.biggest_struggle,
      }).select().single();
      if (newCheckin) setCheckins(prev => {
        const updated = [...prev, newCheckin];
        const totalRevNow = updated.reduce((s, c) => s + (c.ppv_revenue || 0), 0);
        const prevTotal = prev.reduce((s, c) => s + (c.ppv_revenue || 0), 0);
        if (prevTotal < 100 && totalRevNow >= 100) showMilestone("💰", "First $100 earned!", "You crossed the threshold. This gets easier from here.");
        return updated;
      });
      const latestPlan = plans[plans.length - 1];
      const prompt = buildCheckinPrompt(latestPlan, form, weekNum);
      const res = await fetch("/api/generate", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 2000, messages: [{ role: "user", content: prompt }] }),
      });
      if (!res.ok) throw new Error("API error");
      const data = await res.json();
      const text = data.content?.map(b => b.text || "").join("\n") || "";
      const parsed = parsePlan(text);
      await savePlan(parsed, latestPlan.answers, weekNum);
      setCheckinPhase("reveal");
    } catch (err) {
      console.error("Check-in failed:", err);
      setCheckinError("Something went wrong generating your plan. Please try again.");
    } finally {
      setCheckinLoading(false); // FIX: always clears, never leaves spinner stuck
    }
  };

  const handleAuth = async (authUser) => {
    setUser(authUser);
    await supabase.from("profiles").upsert({ id: authUser.id, email: authUser.email });
  };

  const logout = async () => {
    await supabase.auth.signOut();
    // Reset load guard + flags so the next sign-in (even same user) reloads fresh
    loadedUserRef.current = null;
    setUser(null); setPlans([]); setTasks([]); setCheckins([]);
    setIsPaid(false); setDataLoaded(false); setProfile(null);
  };

  if (loading) return <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center" }}><div style={{ color: T.rose, fontFamily: "'DM Mono',monospace", fontSize: "12px" }}>Loading...</div></div>;
  if (!user) return <AuthScreen onAuth={handleAuth} />;
  if (!loading && isPaid && plans.length === 0 && !checkingPaid && dataLoaded) return (
    <NewUserScreen
      userEmail={user.email}
      onLogout={logout}
      welcome={window.location.search.includes("welcome=true")}
      onComplete={async (planData, answers) => { await savePlanOnce(planData, answers, 1); }}
    />
  );
  if (checkingPaid) return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "12px" }}>
      <div style={{ position: "relative", width: "36px", height: "36px" }}>
        <div style={{ position: "absolute", inset: 0, border: "1.5px solid rgba(198,145,115,0.15)", borderTop: `1.5px solid ${T.rose}`, borderRadius: "50%", animation: "spin 0.9s linear infinite" }} />
      </div>
      <div style={{ color: T.rose, fontFamily: "'DM Mono',monospace", fontSize: "11px", letterSpacing: "1px" }}>Activating your account...</div>
      <div style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "10px" }}>This takes a few seconds after payment</div>
    </div>
  );

  if (!isPaid) return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=DM+Mono:wght@400;500&family=DM+Sans:wght@400;500&display=swap');`}</style>
      <div style={{ textAlign: "center", maxWidth: "360px" }}>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "13px", letterSpacing: "4px", color: T.textFaint, marginBottom: "24px" }}>✦ OF COACH ✦</div>
        <div style={{ fontSize: "32px", marginBottom: "16px" }}>🔒</div>
        <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "26px", color: T.cream, marginBottom: "10px" }}>No active subscription</h2>
        <p style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "11px", lineHeight: "1.7", marginBottom: "24px" }}>Your account ({user.email}) doesn't have an active plan.</p>
        {/* Product paused: checkout is disabled — send them to the landing page.
            Sign out first, or the homepage bounces the session back here. */}
        <button
          onClick={async () => {
            await supabase.auth.signOut();
            window.location.href = "/";
          }}
          style={{ display: "inline-block", padding: "13px 28px", background: "linear-gradient(135deg, #c69173, #e0b49a)", borderRadius: "8px", color: "#1a0e10", fontFamily: "'DM Mono',monospace", fontSize: "11px", fontWeight: "700", letterSpacing: "2px", border: "none", cursor: "pointer" }}
        >
          BACK TO HOMEPAGE →
        </button>
        <button onClick={logout} style={{ display: "block", margin: "16px auto 0", background: "none", border: "none", color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "10px", cursor: "pointer" }}>logout</button>
      </div>
    </div>
  );

  // FIX (flicker): paid but data still loading — hold on a spinner instead of
  // flashing the "No plan yet" empty state while plans/tasks are in flight.
  if (!dataLoaded) return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ color: T.rose, fontFamily: "'DM Mono',monospace", fontSize: "12px" }}>Loading your dashboard...</div>
    </div>
  );

  const currentWeek = plans.length;
  const currentPlan = plans[plans.length - 1];
  const currentTasks = tasks.filter(t => t.week_number === currentWeek);
  const completedCount = currentTasks.filter(t => t.completed).length;
  const progress = currentTasks.length > 0 ? Math.round((completedCount / currentTasks.length) * 100) : 0;
  const nextTask = currentTasks.find(t => !t.completed);
  const allTasksDone = currentTasks.length > 0 && completedCount === currentTasks.length;
  const hasCheckedIn = checkins.some(c => c.week_number === currentWeek);
  const totalSubs = checkins.reduce((s, c) => s + (c.new_subs || 0), 0);
  const totalRev = checkins.reduce((s, c) => s + (c.ppv_revenue || 0), 0);
  // Daily streak (replaces the old weekly 50%-tasks streak): computed from
  // profiles.streak_days with 1 freeze per week — see computeDailyStreak.
  const streakInfo = computeDailyStreak(profile?.streak_days);
  const dayStreak = streakInfo.count;
  // Dates of the current Mon–Sun week, for the day-boxes display
  const weekDates = (() => {
    const mon = new Date();
    mon.setDate(mon.getDate() - ((mon.getDay() + 6) % 7));
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(mon.getFullYear(), mon.getMonth(), mon.getDate() + i);
      return localDateStr(d);
    });
  })();
  const todayStr = localDateStr(new Date());
  const streakDaysSet = new Set(profile?.streak_days || []);
  // Freeze consumed on a day of THIS week → show the "streak protected" state
  const streakProtected = dayStreak > 0 && streakInfo.freezeDay && weekDates.includes(streakInfo.freezeDay);

  // Personalized greeting based on performance
  const greeting = (() => {
    if (!currentPlan) return null;
    const firstName = user?.user_metadata?.full_name?.split(" ")[0] || user?.email?.split("@")[0] || "Coach";
    if (currentWeek === 1 && completedCount === 0) return `Welcome, ${firstName}. Your Week 1 plan is ready.`;
    if (allTasksDone) return `${firstName}, you crushed this week. Time to check in.`;
    if (totalRev > 0 && completedCount > 0) return `$${totalRev} earned so far, ${firstName}. Keep the momentum.`;
    if (currentWeek >= 3) return `Week ${currentWeek}, ${firstName}. You're ahead of most creators.`;
    return `Welcome back, ${firstName}. ${completedCount}/${currentTasks.length} tasks done this week.`;
  })();

  return (
    <div style={{ minHeight: "100vh", background: T.bg, color: T.cream }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=DM+Mono:wght@400;500&family=DM+Sans:wght@400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #0e0809; }
        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-thumb { background: rgba(198,145,115,0.25); border-radius: 2px; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes popIn { 0%{transform:scale(0.9);opacity:0;} 60%{transform:scale(1.04);} 100%{transform:scale(1);opacity:1;} }
        button:hover:not(:disabled) { opacity: 0.88; }
        button:active:not(:disabled) { transform: scale(0.98); }
      `}</style>

      <Confetti active={confetti} />

      {/* Milestone celebration toast */}
      {milestone && (
        <div style={{
          position: "fixed", top: "70px", left: "50%", transform: "translateX(-50%)",
          zIndex: 999, background: "#1a0f11", border: `1px solid rgba(198,145,115,0.4)`,
          borderRadius: "14px", padding: "16px 20px", textAlign: "center",
          boxShadow: "0 16px 40px rgba(0,0,0,0.6)", minWidth: "240px",
          animation: "popIn 0.35s ease",
        }}>
          <div style={{ fontSize: "28px", marginBottom: "6px" }}>{milestone.emoji}</div>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "18px", color: T.cream, fontWeight: "700", marginBottom: "3px" }}>{milestone.title}</div>
          <div style={{ fontFamily: "'DM Mono',monospace", fontSize: "10px", color: T.textFaint }}>{milestone.sub}</div>
        </div>
      )}

      {/* Header */}
      <div style={{ borderBottom: `1px solid ${T.border}`, padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", background: T.bg, position: "sticky", top: 0, zIndex: 10 }}>
        <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "16px", letterSpacing: "3px", color: T.textFaint }}>✦ OF COACH ✦</span>
        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
          {/* Daily streak counter */}
          {dayStreak > 0 && (() => {
            const streakLabel = dayStreak >= 90
              ? `🌙 ${Math.floor(dayStreak / 30)}MO STREAK`
              : dayStreak >= 60 ? `💎 ${dayStreak}D STREAK`
              : dayStreak >= 30 ? `👑 ${dayStreak}D STREAK`
              : dayStreak >= 14 ? `🔥 ${dayStreak}D STREAK`
              : dayStreak >= 7  ? `⚡ ${dayStreak}D STREAK`
              : dayStreak >= 3  ? `✦ ${dayStreak}D STREAK`
              : `◇ ${dayStreak}D STREAK`;
            const streakBg = dayStreak >= 90 ? "rgba(180,140,255,0.12)"
              : dayStreak >= 60 ? "rgba(100,200,255,0.12)"
              : dayStreak >= 30 ? "rgba(255,210,50,0.12)"
              : dayStreak >= 14 ? "rgba(255,130,50,0.12)"
              : dayStreak >= 7  ? "rgba(255,200,50,0.10)"
              : dayStreak >= 3  ? "rgba(198,145,115,0.15)"
              : "rgba(255,255,255,0.04)";
            const streakBorder = dayStreak >= 90 ? "rgba(180,140,255,0.35)"
              : dayStreak >= 60 ? "rgba(100,200,255,0.35)"
              : dayStreak >= 30 ? "rgba(255,210,50,0.35)"
              : dayStreak >= 14 ? "rgba(255,130,50,0.35)"
              : dayStreak >= 7  ? "rgba(255,200,50,0.3)"
              : dayStreak >= 3  ? "rgba(198,145,115,0.3)"
              : T.border;
            const streakColor = dayStreak >= 90 ? "#c4a0ff"
              : dayStreak >= 60 ? "#7dd4fc"
              : dayStreak >= 30 ? "#ffd700"
              : dayStreak >= 14 ? "#ff8c42"
              : dayStreak >= 7  ? "#ffe066"
              : dayStreak >= 3  ? T.rose
              : T.textFaint;
            const streakGlow = dayStreak >= 30 ? `0 0 14px ${streakBorder}` : dayStreak >= 7 ? `0 0 8px ${streakBorder}` : "none";
            return (
              <div style={{ background: streakBg, border: `1px solid ${streakBorder}`, borderRadius: "20px", padding: "4px 12px", color: streakColor, fontFamily: "'DM Mono',monospace", fontSize: "9px", letterSpacing: "1.5px", display: "flex", alignItems: "center", gap: "5px", boxShadow: streakGlow }}>
                {streakLabel}{streakProtected ? " 🧊" : ""}
              </div>
            );
          })()}
          {currentWeek > 0 && <div style={{ background: T.roseDim, border: "1px solid rgba(198,145,115,0.25)", borderRadius: "20px", padding: "3px 10px", color: T.rose, fontFamily: "'DM Mono',monospace", fontSize: "9px", letterSpacing: "1px" }}>WEEK {currentWeek}</div>}
          <div style={{ background: "rgba(122,184,152,0.1)", border: "1px solid rgba(122,184,152,0.2)", borderRadius: "20px", padding: "3px 10px", color: T.green, fontFamily: "'DM Mono',monospace", fontSize: "9px", letterSpacing: "1px" }}>PRO ✓</div>
          <a href="https://billing.stripe.com/p/login/5kQ00jg0Ne4w6AxeEod7q00" target="_blank" rel="noopener noreferrer" style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: "6px", color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "9px", padding: "5px 10px", cursor: "pointer", letterSpacing: "1px", textDecoration: "none" }}>BILLING</a>
          <button onClick={logout} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: "6px", color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "9px", padding: "5px 10px", cursor: "pointer", letterSpacing: "1px" }}>LOGOUT</button>
        </div>
      </div>

      <div style={{ maxWidth: "600px", margin: "0 auto", padding: "20px 16px 60px" }}>

        {!currentPlan && (
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <div style={{ fontSize: "40px", marginBottom: "16px" }}>📋</div>
            <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "26px", color: T.cream, marginBottom: "10px" }}>No plan yet</h2>
            <p style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "11px", lineHeight: "1.7" }}>Complete the strategy tool to get your personalized plan.</p>
            <button
              onClick={async () => {
                await supabase.auth.signOut();
                window.location.href = "/";
              }}
              style={{ display: "inline-block", marginTop: "20px", padding: "12px 24px", background: `linear-gradient(135deg, ${T.rose}, ${T.roseLight})`, borderRadius: "8px", color: "#1a0e10", fontFamily: "'DM Mono',monospace", fontSize: "11px", fontWeight: "700", letterSpacing: "2px", border: "none", cursor: "pointer" }}
            >
              GET MY PLAN →
            </button>
          </div>
        )}

        {currentPlan && (<>
          {/* Personalized greeting — mockup-style date eyebrow + serif headline */}
          {greeting && (
            <div style={{ marginBottom: "16px", paddingBottom: "14px", borderBottom: `1px solid ${T.border}` }}>
              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: "10px", letterSpacing: "2px", color: T.rose, textTransform: "uppercase", marginBottom: "6px" }}>
                ✦ {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
              </div>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "26px", color: T.cream, fontWeight: "600", lineHeight: "1.2", letterSpacing: "-0.5px" }}>{greeting}</div>
            </div>
          )}

          {/* Stats — styled to match the homepage mockup stat row */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", marginBottom: checkins.length === 0 ? "10px" : "16px" }}>
            <StatCard label="Subs Gained" value={`+${totalSubs}`} trend={checkins.length > 0 ? `${checkins[checkins.length-1].new_subs} this week` : undefined} sub="from your check-ins" sparkData={checkins.length >= 2 ? checkins.map(c => c.new_subs) : null} />
            <StatCard label="PPV Revenue" value={`$${totalRev}`} highlight trend={checkins.length > 0 ? `$${checkins[checkins.length-1].ppv_revenue} this wk` : undefined} sub="your money made" sparkData={checkins.length >= 2 ? checkins.map(c => c.ppv_revenue) : null} />
            <StatCard label="Tasks" value={`${completedCount}/${currentTasks.length}`} sub={completedCount === 0 ? "start with today's focus" : `${currentTasks.length - completedCount} remaining`} />
          </div>

          {/* Empty state: zeros are a starting line, not a broken product */}
          {checkins.length === 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px", padding: "10px 14px", background: "rgba(198,145,115,0.05)", border: "1px solid rgba(198,145,115,0.15)", borderRadius: "10px" }}>
              <span style={{ color: T.rose, fontSize: "11px" }}>✦</span>
              <span style={{ color: T.textMid, fontFamily: "'DM Mono',monospace", fontSize: "10px", letterSpacing: "1px" }}>DAY 1 — YOUR BASELINE STARTS NOW. Numbers fill in as you log results.</span>
            </div>
          )}

          {/* #17 Onboarding checklist — shown only during week 1 until dismissed */}
          {currentWeek === 1 && (() => {
            const hasFirstTask = completedCount > 0;
            const hasCheckinDone = checkins.length > 0;
            const allStepsDone = hasFirstTask && hasCheckinDone;
            if (allStepsDone) return null;
            return (
              <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: "12px", padding: "14px 16px", marginBottom: "16px" }}>
                <div style={{ fontFamily: "'DM Mono',monospace", fontSize: "9px", color: T.textFaint, letterSpacing: "2px", marginBottom: "12px" }}>GETTING STARTED</div>
                {[
                  { done: true, label: "✦ Got your personalized plan" },
                  { done: hasFirstTask, label: "Complete your first task", action: () => {}, tab: "tasks" },
                  { done: hasCheckinDone, label: "Do your first weekly check-in", tab: "checkin" },
                ].map((step, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px", opacity: step.done ? 0.45 : 1 }}>
                    <div style={{ width: "18px", height: "18px", borderRadius: "50%", border: `1.5px solid ${step.done ? T.green : "rgba(198,145,115,0.4)"}`, background: step.done ? "rgba(122,184,152,0.15)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: "9px", color: T.green }}>
                      {step.done ? "✓" : ""}
                    </div>
                    <span
                      style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "12px", color: step.done ? T.textFaint : T.cream, textDecoration: step.done ? "line-through" : "none", cursor: step.tab && !step.done ? "pointer" : "default" }}
                      onClick={() => step.tab && !step.done && setActiveTab(step.tab)}
                    >{step.label}</span>
                    {!step.done && step.tab && (
                      <span style={{ marginLeft: "auto", fontFamily: "'DM Mono',monospace", fontSize: "9px", color: T.rose, cursor: "pointer" }} onClick={() => setActiveTab(step.tab)}>GO →</span>
                    )}
                  </div>
                ))}
                <div style={{ marginTop: "10px", height: "2px", background: "rgba(255,255,255,0.04)", borderRadius: "2px" }}>
                  <div style={{ height: "100%", borderRadius: "2px", background: `linear-gradient(90deg,${T.rose},${T.roseLight})`, width: `${Math.round(([true, hasFirstTask, hasCheckinDone].filter(Boolean).length / 3) * 100)}%`, transition: "width 0.5s ease" }} />
                </div>
              </div>
            );
          })()}

          {/* Daily micro-task — surfaces the single next action prominently */}
          {nextTask && !allTasksDone && (
            <div style={{ background: "linear-gradient(135deg,rgba(198,145,115,0.08),rgba(198,145,115,0.03))", border: `1px solid rgba(198,145,115,0.25)`, borderRadius: "12px", padding: "14px 16px", marginBottom: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                <span style={{ fontFamily: "'DM Mono',monospace", fontSize: "9px", color: T.rose, letterSpacing: "2px" }}>▶ TODAY'S FOCUS</span>
                <span style={{ fontFamily: "'DM Mono',monospace", fontSize: "8px", color: T.textFaint }}>{completedCount}/{currentTasks.length} DONE</span>
              </div>
              <div style={{ color: T.cream, fontFamily: "'DM Sans',sans-serif", fontSize: "14px", fontWeight: "500", lineHeight: "1.5", marginBottom: "10px" }}>{nextTask.task_text}</div>
              <button onClick={() => toggleTask(nextTask.id, nextTask.completed)} style={{ width: "100%", padding: "10px", borderRadius: "8px", background: `linear-gradient(135deg,${T.rose},${T.roseLight})`, border: "none", color: "#1a0e10", fontFamily: "'DM Mono',monospace", fontSize: "10px", fontWeight: "700", letterSpacing: "2px", cursor: "pointer" }}>MARK DONE ✓</button>
            </div>
          )}

          {/* Daily streak — a day counts when Today's Focus is marked done.
              1 freeze per week: one missed day survives (🧊), a second miss resets. */}
          <div style={{ background: dayStreak >= 7 ? "linear-gradient(135deg,rgba(255,130,50,0.08),rgba(198,145,115,0.06))" : T.surface, border: `1px solid ${dayStreak >= 7 ? "rgba(255,130,50,0.3)" : T.border}`, borderRadius: "12px", padding: "14px 16px", marginBottom: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
              <div>
                <div style={{ fontFamily: "'DM Mono',monospace", fontSize: "9px", color: T.textFaint, letterSpacing: "1.5px", marginBottom: "3px" }}>THIS WEEK</div>
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "20px", color: dayStreak >= 7 ? "#ff8c42" : T.rose, fontWeight: "700" }}>
                  {dayStreak} day streak {dayStreak >= 7 ? "🔥" : dayStreak >= 3 ? "⚡" : "✦"}
                </div>
              </div>
              <div style={{ display: "flex", gap: "5px" }}>
                {["M","T","W","T","F","S","S"].map((d, i) => {
                  const date = weekDates[i];
                  const done = streakDaysSet.has(date);
                  const frozen = date === streakInfo.freezeDay && streakProtected;
                  const isToday = date === todayStr;
                  return (
                    <div key={i} style={{ textAlign: "center" }}>
                      <div style={{ width: "26px", height: "26px", borderRadius: "6px", background: done ? (dayStreak >= 7 ? "#ff8c42" : T.rose) : frozen ? "rgba(100,200,255,0.15)" : "rgba(255,255,255,0.03)", border: `1px solid ${isToday && !done ? "rgba(198,145,115,0.4)" : frozen ? "rgba(100,200,255,0.35)" : "transparent"}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px" }}>{done ? "✓" : frozen ? "🧊" : ""}</div>
                      <div style={{ fontFamily: "'DM Mono',monospace", fontSize: "8px", color: isToday ? T.rose : T.textFaint, marginTop: "3px" }}>{d}</div>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Motivational nudge based on streak state */}
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: "9px", color: streakProtected ? "#7dd4fc" : T.textFaint, letterSpacing: "0.5px", borderTop: `1px solid ${T.border}`, paddingTop: "8px" }}>
              {streakProtected && "🧊 Streak protected — your weekly freeze covered a missed day. Don't miss another this week."}
              {!streakProtected && dayStreak === 0 && "◇ Mark today's focus done to start your streak."}
              {!streakProtected && dayStreak > 0 && !streakInfo.doneToday && "✦ Today's focus is still open — complete it to keep the streak alive."}
              {!streakProtected && dayStreak === 1 && streakInfo.doneToday && "✦ Day 1 done. Come back tomorrow to keep it going."}
              {!streakProtected && dayStreak === 2 && streakInfo.doneToday && "✦ 2 days in. Don't break it now."}
              {!streakProtected && dayStreak >= 3 && dayStreak < 7 && streakInfo.doneToday && "⚡ You're building a real habit. One freeze per week has your back."}
              {!streakProtected && dayStreak >= 7 && streakInfo.doneToday && "🔥 A full week straight. This is what separates real creators."}
            </div>
          </div>

          {/* All done banner */}
          {allTasksDone && !hasCheckedIn && (
            <div style={{ background: T.greenDim, border: "1px solid rgba(122,184,152,0.25)", borderRadius: "12px", padding: "20px", textAlign: "center", marginBottom: "16px", animation: "popIn 0.4s ease" }}>
              <div style={{ fontSize: "26px", marginBottom: "8px" }}>🎉</div>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "20px", color: T.green, fontWeight: "700", marginBottom: "6px" }}>All {currentTasks.length} tasks done!</div>
              <div style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "10px", marginBottom: "14px" }}>Check in to unlock your Week {currentWeek + 1} strategy.</div>
              <button onClick={() => setActiveTab("checkin")} style={{ padding: "11px 24px", borderRadius: "8px", background: `linear-gradient(135deg, ${T.rose}, ${T.roseLight})`, border: "none", color: "#1a0e10", fontFamily: "'DM Mono',monospace", fontSize: "10px", fontWeight: "700", letterSpacing: "1.5px", cursor: "pointer" }}>DO MY CHECK-IN →</button>
            </div>
          )}

          {/* Progress bar */}
          <div style={{ marginBottom: "20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: "10px", color: T.textFaint, letterSpacing: "1px" }}>WEEK {currentWeek} PROGRESS</span>
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: "10px", color: T.rose }}>{completedCount}/{currentTasks.length} · {progress}%</span>
            </div>
            <div style={{ height: "4px", background: "rgba(198,145,115,0.08)", borderRadius: "2px" }}>
              <div style={{ height: "100%", borderRadius: "2px", width: `${progress}%`, background: `linear-gradient(90deg, ${T.rose}, ${T.roseLight})`, transition: "width 0.5s cubic-bezier(0.4,0,0.2,1)", boxShadow: "0 0 8px rgba(198,145,115,0.25)" }} />
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", gap: "4px", marginBottom: "20px", background: "rgba(255,255,255,0.03)", borderRadius: "10px", padding: "4px" }}>
            {[
              { id: "plan", label: "MY PLAN" },
              { id: "tasks", label: `TASKS ${completedCount}/${currentTasks.length}` },
              { id: "ideas", label: "✦ IDEAS" },
              { id: "pricing", label: "💰 PRICING" },
              { id: "progress", label: "PROGRESS" },
              // Check-in only shown once all tasks for the week are done
              ...(allTasksDone ? [{ id: "checkin", label: hasCheckedIn ? "✓ DONE" : "CHECK-IN 🔥" }] : []),
            ].map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)} style={{ flex: 1, padding: "8px 2px", borderRadius: "7px", border: "none", background: activeTab === t.id ? `linear-gradient(135deg, ${T.rose}, ${T.roseLight})` : "transparent", color: activeTab === t.id ? "#0e0809" : T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "8px", fontWeight: "700", letterSpacing: "1px", cursor: "pointer", transition: "all 0.2s", whiteSpace: "nowrap" }}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Plan tab */}
          {activeTab === "plan" && currentPlan.plan_data && (
            <div>
              <div style={{ marginBottom: "16px", display: "flex", alignItems: "center", gap: "10px" }}>
                <div style={{ flex: 1, height: "1px", background: T.border }} />
                <span style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "9px", letterSpacing: "2px" }}>WEEK {currentWeek} STRATEGY</span>
                <div style={{ flex: 1, height: "1px", background: T.border }} />
              </div>
              {(() => {
                // Dynamic HIGH IMPACT badge — shifts based on their numbers
                const avgRevPerWeek = checkins.length > 0 ? totalRev / checkins.length : 0;
                const avgSubsPerWeek = checkins.length > 0 ? totalSubs / checkins.length : 0;
                const ppvBadge = avgRevPerWeek < 300 ? "HIGH IMPACT" : null;
                const promoBadge = avgSubsPerWeek < 10 ? "HIGH IMPACT" : null;
                const pricingBadge = avgRevPerWeek >= 300 && avgSubsPerWeek >= 10 ? "OPTIMIZE NOW" : null;
                return (<>
                  {/* Empty state: never render fabricated numbers — CTA to the quiz instead */}
                  {!currentPlan.plan_data.diagnosis && (
                    <div style={{ border: `1px dashed ${T.border}`, borderRadius: "12px", marginBottom: "10px", padding: "20px 18px", background: T.surface, textAlign: "center" }}>
                      <div style={{ fontFamily: "'Cormorant Garamond',serif", fontStyle: "italic", color: T.cream, fontSize: "16px", marginBottom: "10px" }}>Your diagnosis isn't built yet</div>
                      <button onClick={() => setActiveTab("pricing")} style={{ background: "none", border: "1px solid rgba(198,145,115,0.3)", borderRadius: "8px", padding: "9px 16px", color: T.rose, fontFamily: "'DM Mono',monospace", fontSize: "9px", letterSpacing: "1.5px", cursor: "pointer" }}>
                        COMPLETE THE PRICING QUIZ TO GENERATE IT →
                      </button>
                    </div>
                  )}
                  <SectionCard emoji="🔍" title="Your Diagnosis" content={currentPlan.plan_data.diagnosis} highlight defaultOpen />
                  <SectionCard emoji="⚡" title="Do This Today" content={currentPlan.plan_data.today} />
                  <SectionCard emoji="💰" title="Pricing Strategy" content={currentPlan.plan_data.pricing} badge={pricingBadge} />
                  <SectionCard emoji="🔥" title="PPV Playbook" content={currentPlan.plan_data.ppv} badge={ppvBadge} />
                  <SectionCard emoji="📣" title="Promotion Plan" content={currentPlan.plan_data.promotion} badge={promoBadge} />
                  <SectionCard emoji="📅" title="7-Day Plan" content={currentPlan.plan_data.week} />
                  <SectionCard emoji="💬" title="Real Talk" content={currentPlan.plan_data.realtalk} />
                </>);
              })()}
            </div>
          )}

          {/* Tasks tab */}
          {activeTab === "tasks" && (
            <div>
              {currentTasks.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 20px", border: `1px dashed ${T.border}`, borderRadius: "12px", background: T.surface }}>
                  <div style={{ fontSize: "26px", marginBottom: "10px" }}>📋</div>
                  <div style={{ color: T.cream, fontFamily: "'Cormorant Garamond',serif", fontSize: "18px", fontWeight: "600", marginBottom: "6px" }}>Your starting line</div>
                  <div style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "10px", lineHeight: "1.8", marginBottom: "14px" }}>Your tasks will appear here after your Week 1 plan.</div>
                  {!profile?.pricing_quiz_data && (
                    <button onClick={() => setActiveTab("pricing")} style={{ background: "none", border: "1px solid rgba(198,145,115,0.3)", borderRadius: "8px", padding: "9px 16px", color: T.rose, fontFamily: "'DM Mono',monospace", fontSize: "9px", letterSpacing: "1.5px", cursor: "pointer" }}>
                      START WITH THE PRICING QUIZ →
                    </button>
                  )}
                </div>
              ) : (() => {
                const sorted = [...currentTasks].sort((a, b) => (a.created_at > b.created_at ? 1 : -1));
                const completedCount = sorted.filter(t => t.completed).length;
                const pct = Math.round((completedCount / sorted.length) * 100);
                return (
                  <div>
                    {/* Progress bar */}
                    <div style={{ marginBottom: "20px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                        <span style={{ fontFamily: "'DM Mono',monospace", fontSize: "10px", color: T.textFaint, letterSpacing: "1px" }}>WEEK PROGRESS</span>
                        <span style={{ fontFamily: "'DM Mono',monospace", fontSize: "11px", color: pct === 100 ? T.green : T.rose, fontWeight: "700" }}>{completedCount}/{sorted.length} · {pct}%</span>
                      </div>
                      <div style={{ height: "4px", background: "rgba(255,255,255,0.06)", borderRadius: "4px", overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${pct}%`, background: pct === 100 ? T.green : `linear-gradient(90deg, ${T.rose}, #e0b49a)`, borderRadius: "4px", transition: "width 0.4s ease" }} />
                      </div>
                      {pct === 100 && (
                        <div style={{ marginTop: "10px", textAlign: "center", color: T.green, fontFamily: "'DM Mono',monospace", fontSize: "10px", letterSpacing: "1px" }}>
                          🏆 WEEK COMPLETE — YOU CRUSHED IT
                        </div>
                      )}
                    </div>
                    {sorted.map((task, i) => (
                      <TaskItem key={task.id} task={task} onToggle={toggleTask} isNext={task.id === nextTask?.id} dayNumber={i + 1} />
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Progress tab */}
          {activeTab === "progress" && (
            <div>
              <RevenueGoalTracker totalRev={totalRev} checkins={checkins} userId={user.id} />
              {/* #15: Quick revenue log — manual entry without doing a full check-in */}
              <QuickRevenueLog userId={user.id} onSaved={(entry) => {
                setCheckins(prev => [...prev, entry]);
                const newTotal = [...checkins, entry].reduce((s,c) => s + (c.ppv_revenue||0), 0);
                if (totalRev < 100 && newTotal >= 100) showMilestone("💰", "First $100 earned!", "You crossed the threshold. This gets easier from here.");
              }} />
              <GrowthChart checkins={checkins} />
              <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: "12px", padding: "16px", marginBottom: "16px" }}>
                <div style={{ fontFamily: "'DM Mono',monospace", fontSize: "10px", color: T.textFaint, letterSpacing: "1.5px", marginBottom: "14px" }}>HOW YOU COMPARE — CREATORS AT YOUR STAGE</div>
                <BenchmarkBar label="SUBS GAINED" yours={totalSubs} average={8} best={31} />
                <BenchmarkBar label="PPV REVENUE" yours={totalRev} average={22} best={180} unit="$" />
                <BenchmarkBar label="TASKS COMPLETED" yours={completedCount} average={4} best={7} />
                <div style={{ marginTop: "12px", padding: "10px 12px", background: "rgba(198,145,115,0.05)", borderRadius: "8px" }}>
                  <span style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "9px" }}>
                    {totalSubs > 15 ? "You're outperforming most new creators. Keep this momentum." : "Complete your tasks consistently — most creators who do are earning $500+/mo by week 8."}
                  </span>
                </div>
              </div>
              <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: "12px", padding: "16px" }}>
                <div style={{ fontFamily: "'DM Mono',monospace", fontSize: "10px", color: T.textFaint, letterSpacing: "1.5px", marginBottom: "14px" }}>PROJECTED GROWTH</div>
                {[
                  { label: "By Week 4", subs: "35–50 subs", revenue: "$150–$400/mo" },
                  { label: "By Week 8", subs: "80–120 subs", revenue: "$500–$900/mo" },
                  { label: "By Week 12", subs: "150–200 subs", revenue: "$1,000–$2,000/mo" },
                ].map((p, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: i < 2 ? `1px solid ${T.border}` : "none" }}>
                    <div>
                      <div style={{ color: T.rose, fontFamily: "'DM Mono',monospace", fontSize: "9px", letterSpacing: "1px", marginBottom: "2px" }}>{p.label}</div>
                      <div style={{ color: T.textFaint, fontFamily: "'DM Sans',sans-serif", fontSize: "12px" }}>{p.subs}</div>
                    </div>
                    <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "20px", color: T.cream, fontWeight: "700" }}>{p.revenue}</div>
                  </div>
                ))}
                <div style={{ marginTop: "10px", color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "9px", lineHeight: "1.6" }}>* Based on your trajectory and creator averages. Requires consistent execution.</div>
              </div>
            </div>
          )}

          {/* Check-in tab */}
          {/* Ideas tab */}
          {activeTab === "ideas" && (
            <IdeasTab plan={currentPlan.plan_data} answers={currentPlan.answers} weekNumber={currentWeek} />
          )}

          {/* Pricing tab */}
          {activeTab === "pricing" && (
            <PricingTab
              userId={user?.id}
              userEmail={user?.email}
              profile={profile}
              refreshProfile={loadProfile}
            />
          )}

          {activeTab === "checkin" && (
            <div>
              {/* FIX: show error message if check-in API call fails */}
              {checkinError && (
                <div style={{ background: "rgba(196,85,106,0.1)", border: "1px solid rgba(196,85,106,0.3)", borderRadius: "8px", padding: "12px 14px", marginBottom: "16px", color: "#c4556a", fontFamily: "'DM Mono',monospace", fontSize: "11px" }}>
                  {checkinError}
                  <button onClick={() => setCheckinError(null)} style={{ marginLeft: "12px", background: "none", border: "none", color: "#c4556a", cursor: "pointer", fontSize: "11px", textDecoration: "underline" }}>dismiss</button>
                </div>
              )}
              {hasCheckedIn ? (
                <div style={{ textAlign: "center", padding: "48px 20px", background: T.greenDim, border: "1px solid rgba(122,184,152,0.2)", borderRadius: "12px" }}>
                  <div style={{ fontSize: "32px", marginBottom: "12px" }}>✅</div>
                  <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "22px", color: T.green, fontWeight: "700", marginBottom: "8px" }}>Week {currentWeek} check-in done.</div>
                  <div style={{ color: T.textFaint, fontFamily: "'DM Mono',monospace", fontSize: "10px", lineHeight: "1.7" }}>Your Week {currentWeek + 1} plan is live.<br />Come back next Sunday for your next check-in.</div>
                </div>
              ) : checkinPhase === "reveal" ? (
                <PlanReveal checkins={checkins} weekNum={plans.length} onDone={() => { setCheckinPhase("done"); setActiveTab("plan"); }} />
              ) : (
                <CheckinWizard onSubmit={handleCheckin} loading={checkinLoading} weekNum={plans.length + 1} />
              )}
            </div>
          )}
        </>)}
      </div>
    </div>
  );
}
